import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import Index from "../../../../component/Index";
import { Controller } from "react-hook-form";
import ContainerIndex from "../../../Index";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import moment from "moment";
import { toast } from "react-toastify";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import dayjs from "dayjs";
import {
  Autocomplete,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { useReactToPrint } from "react-to-print";
import ExcelJS from "exceljs";
import LoadingSpinner from "../../../../component/LoadingSpinner";
import { styled } from "@mui/material/styles";
import {
  getWebViewAeriaWiseFilterOptionsApiHandler,
  getWebViewCompanyAddressApiHandler,
  postWebViewOutstandingMasterReportDataApiHandler,
  uploadPdfApiHandler,
} from "../../../../services/AdminIndex";
import Spinner from "../../../../component/Spinner";
import { useParams } from "react-router-dom";

const Item = styled(Index.Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode == "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

// static data for dropdown
const selectData = [
  { id: "all", value: "ALL", type: "All" },
  { id: "selected", value: "SELECTED", type: "selected" },
];
const stockTypeList = [
  { id: "outstanding", value: "BILL_O/S", show: "Bill O/s" },
  { id: "all", value: "ALL_BILLS", show: "All Bills " },
  { id: "date", value: "DATE_TO_DATE_O/S", show: "Date To Date O/s" },
];
const outStandingData = [
  {
    id: "customer",
    value: "Customer OutStanding Report",
    partyName: "Customer OutStanding Report",
  },
  {
    id: "supplier",
    value: "Supplier Outstanding Report",
    partyName: "Supplier Outstanding Report",
  },
  {
    id: "customerbroker",
    value: "Customer Outstanding Report (BrokerWise)",
    partyName: "Customer Outstanding Report (BrokerWise)",
  },
  {
    id: "supplierbroker",
    value: "Supplier Outstanding Report (BrokerWise)",
    partyName: "Supplier Outstanding Report (BrokerWise)",
  },
  {
    id: "customeragewise",
    value: "Customer Outstanding Report (Age Wise Analysis)",
    partyName: "Customer Outstanding Report (Age Wise Analysis)",
  },
  {
    id: "supplieragewise",
    value: "Supplier Outstanding Report (Age Wise Analysis)",
    partyName: "Supplier Outstanding Report (Age Wise Analysis)",
  },
  {
    id: "customermonthwise",
    value: "Customer Outstanding Monthly Summary",
    partyName: "Customer Outstanding Monthly Summary",
  },
  {
    id: "suppliermonthwise",
    value: "Supplier Outstanding Monthly Summary",
    partyName: "Supplier Outstanding Monthly Summary",
  },
];

const parseData = (data) => {
  try {
    // if plain js
    if (data) {
      let result = JSON.parse(data);
      return result;
    } else {
      return [];
    }
  } catch (e) {
    return [];
  }
};

const formatDate = (date, type) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  if (type == "print") {
    return `${day}-${month}-${year}`;
  } else {
    return `${year}-${month}-${day}`;
  }
};

const formatArrayForApi = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return "";
  return arr.join(",");
};

const getSafeNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined) return defaultValue;
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

const isCustomerReport = (reportType) => reportType?.includes("customer");
const isSupplierReport = (reportType) => reportType?.includes("supplier");
const isBrokerWise = (reportType) => reportType?.includes("broker");
const isAgeWise = (reportType) => reportType?.includes("agewise");
const isMonthWise = (reportType) => reportType?.includes("monthwise");

const isAdvanceRow = (sale, reportType) => {
  if (sale?.status === "advance") return true;
  if (sale?.balType !== "openingBalance") return false;
  const upperType = sale?.type?.toUpperCase();
  return (
    (isCustomerReport(reportType) && upperType === "C") ||
    (isSupplierReport(reportType) && upperType === "D")
  );
};

const isOpeningBalanceTop = (sale, reportType) => {
  if (sale?.balType !== "openingBalance") return false;
  return (
    (reportType === "customer" && sale?.type === "D") ||
    (reportType === "supplier" && sale?.type === "C")
  );
};

const calcAmount = (sale, reportType) => {
  if (sale?.monthComplete) return 0;
  if (isAdvanceRow(sale, reportType)) return 0;
  return getSafeNumber(sale?.totalAmount);
};

const calcReceivedAmount = (sale, reportType) => {
  if (sale?.monthComplete) return 0;
  if (sale?.transactionType === "CR") {
    return getSafeNumber(sale?.amount);
  }
  if (isAdvanceRow(sale, reportType)) {
    return getSafeNumber(sale?.balanceAmount || sale?.amount);
  }
  // Opening balance bills: amount/orignalBillAmount never change after creation,
  // and every match against the bill is reliably recorded in accountData —
  // so received = (bill total - opening pending amount) + everything matched since.
  // This avoids depending on outstandingVoucher.balanceAmount/recAmmount, which
  // aren't consistently populated across historical entries.
  if (sale?.balType === "openingBalance") {
    const baseline = getSafeNumber(sale?.totalAmount) - Math.abs(getSafeNumber(sale?.amount));
    const matched = getSafeNumber(
      sale?.accountData?.reduce((acc, account) => acc + getSafeNumber(account?.amount), 0)
    );
    return Math.max(0, baseline + matched);
  }
  if (sale?.amount && sale?.amount > 0 && sale?.totalAmount) {
    return getSafeNumber(sale?.totalAmount) - getSafeNumber(sale?.amount);
  }
  return getSafeNumber(
    sale?.accountData?.reduce(
      (acc, account) => acc + getSafeNumber(account?.amount),
      0
    )
  );
};

const calcDiscountAmount = (sale) => {
  if (sale?.monthComplete) return 0;
  return getSafeNumber(
    sale?.accountData?.reduce((acc, account) => {
      if (account?.salesReference === "cashBank") {
        return acc + getSafeNumber(account?.discount);
      }
      return acc;
    }, 0)
  );
};

const calcSrPrAmount = (sale, reportType) => {
  if (sale?.monthComplete) return 0;
  if (sale?.balType === "openingBalance") return 0;
  return getSafeNumber(
    sale?.accountData?.reduce((acc, account) => {
      const isCustomerSr =
        isCustomerReport(reportType) &&
        (account?.salesReference === "salesReturn" ||
          account?.salesReference === "creditNote");
      const isSupplierPr =
        isSupplierReport(reportType) &&
        (account?.salesReference === "purchaseReturn" ||
          account?.salesReference === "debitNotePurchase");
      return isCustomerSr || isSupplierPr
        ? acc + getSafeNumber(account?.amount)
        : acc;
    }, 0)
  );
};

const calcBalanceAmount = (sale, reportType) => {
  if (sale?.monthComplete) return 0;
  const totalAmount = getSafeNumber(sale?.totalAmount);
  const receivedAmount = calcReceivedAmount(sale, reportType);
  return Math.max(0, totalAmount - receivedAmount);
};

const calcVoucherAmount = (sale) => {
  if (sale?.monthComplete) return 0;
  return getSafeNumber(
    sale?.accountData?.reduce(
      (acc, account) => acc + getSafeNumber(account?.amount),
      0
    )
  );
};

const sumPartyField = (salesData, calcFn, reportType) => {
  if (!salesData?.length) return 0;
  return salesData.reduce((acc, sale) => acc + calcFn(sale, reportType), 0);
};

const sumGrandTotalField = (filterReportData, calcFn, reportType) => {
  if (!filterReportData?.length) return 0;
  return filterReportData.reduce((reportSum, report) => {
    return reportSum + sumPartyField(report?.salesData, calcFn, reportType);
  }, 0);
};

const sumBrokerPartyField = (salesDetails, calcFn, reportType) => {
  if (!salesDetails?.length) return 0;
  return salesDetails.reduce(
    (acc, detail) => acc + calcFn(detail, reportType),
    0
  );
};

const sumBrokerGrandTotalField = (filterReportData, calcFn, reportType) => {
  if (!filterReportData?.length) return 0;
  return filterReportData.reduce((brokerSum, broker) => {
    const partiesSum =
      broker?.salesData?.reduce((partySum, party) => {
        return (
          partySum +
          sumBrokerPartyField(party?.salesDetails, calcFn, reportType)
        );
      }, 0) || 0;
    return brokerSum + partiesSum;
  }, 0);
};

const calcOddDays = (sale) => {
  if (!sale?.invoiceDate) return "";
  const today = moment().startOf("day");
  const invoiceDate = moment(sale?.invoiceDate).startOf("day");
  const creditDays = getSafeNumber(sale?.creditDays);
  const dueDate = invoiceDate.clone().add(creditDays, "days");
  return today.diff(dueDate, "days");
};

const formatNumber = (num) => getSafeNumber(num).toFixed(2);
const formatDateDisplay = (date) =>
  date ? moment(date).format("DD-MM-YYYY") : "";

export default function WebViewOutstandingReport() {
  const dispatch = Index.useDispatch();
  const { loading } = Index.useSelector((state) => {
    return state.ERPAdminReducer;
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    watch,
  } = Index.useForm({
    defaultValues: {
      customer: "all",
      brokerType: "all",
      show: "",
      ageSummary: false,
      AgeWiseAnalysis: false,
      till: dayjs(),
      WithAdvance: true,
      selectAreaWise: [],
      selectCityWise: [],
      selectStateWise: [],
      select_group: [],
      IgnoreCrDays: false,
    },
  });

  const { companyId, selectedYearStart, selectedYearEnd } = useParams();
  const [isLoading, setIdLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [brokerReportData, setBrokerReportData] = useState([]);
  const [filterReportData, setFilterReportData] = useState([]);
  const [areawise, setAreaWise] = useState([]);
  const [stateWise, setStateWise] = useState([]);
  const [cityWise, setCityWise] = useState([]);
  const [broker, setBroker] = useState([]);
  const [companyAddress, setCompanyAddress] = useState({});
  const [saveButtonLoader, setSaveButtonLoader] = useState(false);

  const [filterWiseCustomerParty, setFilterWiseCustomerParty] = useState([]);
  const [filterWiseSupplierParty, setFilterWiseSupplierParty] = useState([]);
  const [partyWiseCustomer, setPartyWiseCustomer] = useState([]);
  const [partySupplierWiseCustomer, setPartySupplierWiseCustomer] = useState(
    []
  );
  const [groupWise, setGroupWise] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [dateHeader, setDateHeader] = useState([]);
  const [reportFileName, setReportFileName] = useState("Outstanding_Report");

  const partyList = [
    { id: "areawise", value: "AREA_WISE", partyName: "Area Wise" },
    { id: "citywise", value: "CITY_WISE", partyName: "City Wise" },
    { id: "statewise", value: "STATE_WISE", partyName: "State Wise" },
  ];

  const filterRef = useRef();
  const printRef = useRef();

  const watchBetween = watch("between");
  const watchFromDay = watch("fromDay");
  const watchToDay = watch("toDay");
  const watchIgnoreCrDays = watch("IgnoreCrDays");
  const watchMonthWiseTotal = watch("monthWiseTotal");
  const watchReportType = watch("outStandingReport");
  const watchWithAdvance = watch("WithAdvance");

  // Fix 1: Update filterValues memo to include show
  const filterValues = useMemo(() => ({
    between: watchBetween,
    fromDay: watchFromDay,
    toDay: watchToDay,
    ignoreCrDays: watchIgnoreCrDays,
    monthWiseTotal: watchMonthWiseTotal,
    reportType: watchReportType,
    withAdvance: watchWithAdvance,
  }), [
    watchBetween,
    watchFromDay,
    watchToDay,
    watchIgnoreCrDays,
    watchMonthWiseTotal,
    watchReportType,
    watchWithAdvance,
  ]);
  const checkFilterConditions = useCallback((sale, filterVals) => {
    if (sale?.monthComplete) return true;
    if (sale?.balType === "openingBalance") return true;
    if (sale?.status === "advance") return true;

    const diffDays = calcOddDays(sale);
    if (diffDays === "") return true;

    // Bill O/S + IgnoreCrDays false = negative O/D rows exclude 
    const dateCondition = filterVals.ignoreCrDays ? true : diffDays > 0;

    let betweenCondition = true;
    if (filterVals.between) {
      const fromDay = getSafeNumber(filterVals.fromDay);
      const toDay = getSafeNumber(filterVals.toDay);
      if (fromDay !== 0 || toDay !== 0) {
        betweenCondition = diffDays >= fromDay && diffDays <= toDay;
      }
    }

    return dateCondition && betweenCondition;
  }, []);

  const sortSalesData = useCallback((data, reportType) => {
    return [...data].sort((a, b) => {
      if (a?.monthComplete && b?.monthComplete) return 0;
      if (a?.monthComplete) return 1;
      if (b?.monthComplete) return -1;

      const aIsTop = isOpeningBalanceTop(a, reportType);
      const bIsTop = isOpeningBalanceTop(b, reportType);
      if (aIsTop && !bIsTop) return -1;
      if (!aIsTop && bIsTop) return 1;

      const aIsBottom = isAdvanceRow(a, reportType);
      const bIsBottom = isAdvanceRow(b, reportType);
      if (aIsBottom && !bIsBottom) return 1;
      if (!aIsBottom && bIsBottom) return -1;

      return moment(a.invoiceDate || a.date) - moment(b.invoiceDate || b.date);
    });
  }, []);

  const createMonthWiseSummary = (salesList, monthKey, reportType) => {
    if (!salesList?.length) return null;
    const totals = salesList.reduce(
      (acc, sale) => {
        const totalAmount = calcAmount(sale, reportType);
        const receivedAmount = calcReceivedAmount(sale, reportType);

        acc.totalAmount += totalAmount;
        acc.receivedAmount += receivedAmount;
        acc.discountAmount += calcDiscountAmount(sale);
        acc.srPrAmount += calcSrPrAmount(sale, reportType);
        acc.balanceAmount += (totalAmount - receivedAmount);

        return acc;
      },
      {
        totalAmount: 0,
        receivedAmount: 0,
        discountAmount: 0,
        srPrAmount: 0,
        balanceAmount: 0,
      }
    );

    totals.balanceAmount = Math.max(0, totals.balanceAmount);

    const [year, month] = monthKey?.split("-") || [];
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const monthName = monthNames[parseInt(month, 10) - 1] || "";
    const displayText = `${monthName}-${year}`;

    return {
      monthComplete: true,
      monthKey: monthKey,
      monthDisplayText: displayText,
      MonthWiseTotalAmount: totals.totalAmount,
      MonthWiseReceivedAmount: totals.receivedAmount,
      MonthWiseDiscount: totals.discountAmount,
      MonthWiseSrPrAmount: totals.srPrAmount,
      MonthWiseBalanceAmount: totals.balanceAmount,
    };
  };

  const getReportFileName = useCallback(() => {
    const selectedCustomers = watch("selectCustomerWise");
    const selectedSuppliers = watch("selectSupplierWise");
    const selectedBroker = watch("broker");
    const reportType = watch("outStandingReport");

    let partyName = null;

    if (isBrokerWise(reportType) && selectedBroker && watch("brokerType") === "selected") {
      const brokerObj = broker?.find((b) => b?.id === selectedBroker);
      partyName = brokerObj?.accountName;
    }
    else if (isCustomerReport(reportType) && selectedCustomers?.length === 1) {
      const customer = filterWiseCustomerParty?.find((c) => c?.id === selectedCustomers[0]);
      partyName = customer?.accountName;
    }
    else if (isSupplierReport(reportType) && !isBrokerWise(reportType) && selectedSuppliers?.length === 1) {
      const supplier = filterWiseSupplierParty?.find((s) => s?.id === selectedSuppliers[0]);
      partyName = supplier?.accountName;
    }

    if (partyName) {
      const cleanName = partyName
        .replace(/[^a-zA-Z0-9_\- ]/g, "")
        .replace(/\s+/g, "_")
        .replace(/_+/g, "_")
        .trim();
      return cleanName ? `${cleanName}_Outstanding_Report` : "Outstanding_Report";
    }

    return "Outstanding_Report";
  }, [
    watch("selectCustomerWise"),
    watch("selectSupplierWise"),
    watch("broker"),
    watch("brokerType"),
    watch("outStandingReport"),
    filterWiseCustomerParty,
    filterWiseSupplierParty,
    broker,
  ]);

  useEffect(() => {
    setReportFileName(getReportFileName());
  }, [getReportFileName]);

  useEffect(() => {
    setValue("startDate", dayjs(selectedYearStart));
    setValue("endDate", dayjs(selectedYearEnd));
  }, [selectedYearStart, selectedYearEnd]);

  //Back to top
  const handleScrollToTop = (ref) => {
    filterRef.current.scrollIntoView();
  };

  //Export to Excel
  const exportToExcel = async () => {
    if (!filterReportData?.length) {
      toast.error("No data available to export.");
      return;
    }

    const reportType = watch("outStandingReport");
    const extraColumns = {
      showPaymentDetails: watch("printWithPaymentDetails"),
      showPoNumber: watch("poNumber"),
      showCategory: watch("category"),
    };

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Outstanding Report", {
      properties: { defaultColWidth: 15 },
      pageSetup: {
        paperSize: 9,
        orientation: "landscape",
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
        margins: {
          left: 0.4,
          right: 0.4,
          top: 0.5,
          bottom: 0.5,
          header: 0.3,
          footer: 0.3,
        },
      },
    });

    // ========== COLOR SCHEME (UI matched) ==========
    const C = {
      companyBg: "FF1A3C5E",
      companyFont: "FFFFFFFF",
      titleBg: "FF2E6DA4",
      titleFont: "FFFFFFFF",
      dateBg: "FFD6E4F0",
      dateFont: "FF1A3C5E",
      colHeaderBg: "FF2E6DA4",
      colHeaderFont: "FFFFFFFF",
      brokerBg: "FFD0D0D0",
      brokerFont: "FF000000",
      partyBg: "FFDFF0D8",
      partyFont: "FF2D6A4F",
      oddBg: "FFFFFFFF",
      evenBg: "FFF2F7FF",
      normalFont: "FF2C3E50",
      advanceBg: "FFFDE8E8",
      advanceFont: "FFC0392B",
      monthBg: "FFEAF4FB",
      monthFont: "FF1A5276",
      partyTotalBg: "FFFFF3CD",
      partyTotalFont: "FF7D6608",
      grandBg: "FF2E6DA4",
      grandFont: "FFFFFFFF",
      border: "FFB2BEC3",
    };

    const makeBorder = (color = C.border) => ({
      top: { style: "thin", color: { argb: color } },
      left: { style: "thin", color: { argb: color } },
      bottom: { style: "thin", color: { argb: color } },
      right: { style: "thin", color: { argb: color } },
    });

    // Apply style to all cells in a row
    const styleRow = (row, bgArgb, fontArgb, bold = false, numFmt = null, totalCols) => {
      for (let c = 1; c <= totalCols; c++) {
        const cell = row.getCell(c);
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgArgb } };
        cell.font = { name: "Calibri", size: 10, bold, color: { argb: fontArgb } };
        cell.border = makeBorder();
        cell.alignment = { vertical: "middle", wrapText: false };
        if (numFmt && typeof cell.value === "number") cell.numFmt = numFmt;
      }
    };

    const numFmt = "#,##0.00";

    let colDefs = [
      { header: "Bill No.", key: "billNo", width: 16 },
      { header: "Bill Date", key: "billDate", width: 13 },
      { header: "Amount", key: "amount", width: 14 },
      { header: "Received Amt.", key: "received", width: 14 },
      { header: "Discount Amt.", key: "discount", width: 14 },
      {
        header: isCustomerReport(reportType) ? "SR Amt." : "PR Amt.",
        key: "srpr",
        width: 13,
      },
    ];
    if (extraColumns.showPaymentDetails) {
      colDefs.push(
        { header: "Voucher No.", key: "vNo", width: 14 },
        { header: "Voucher Date", key: "vDate", width: 13 },
        { header: "Voucher Amt.", key: "vAmt", width: 14 }
      );
    }
    colDefs.push(
      { header: "Balance Amt.", key: "balance", width: 14 },
      { header: "Due Date", key: "dueDate", width: 13 },
      { header: "O/D Days", key: "odDays", width: 10 }
    );
    if (extraColumns.showCategory) colDefs.push({ header: "Category", key: "cat", width: 18 });
    if (extraColumns.showPoNumber) colDefs.push({ header: "P.O Number", key: "po", width: 16 });

    const totalCols = colDefs.length;
    worksheet.columns = colDefs.map((c) => ({ width: c.width }));

    const tillDate = watch("till")
      ? watch("till").format("DD-MM-YYYY")
      : moment().format("DD-MM-YYYY");

    // ── Row 1: Company Name ──────────────────────────────────
    worksheet.mergeCells(1, 1, 1, totalCols);
    const r1 = worksheet.getRow(1);
    r1.height = 28;
    r1.getCell(1).value = companyAddress?.Company_name || "Company Name";
    styleRow(r1, C.companyBg, C.companyFont, true, null, totalCols);
    r1.getCell(1).font = { name: "Calibri", size: 14, bold: true, color: { argb: C.companyFont } };
    r1.getCell(1).alignment = { vertical: "middle", horizontal: "center" };

    // ── Row 2: Report Title ──────────────────────────────────
    worksheet.mergeCells(2, 1, 2, totalCols);
    const r2 = worksheet.getRow(2);
    r2.height = 22;
    r2.getCell(1).value = isBrokerWise(reportType)
      ? `${isCustomerReport(reportType) ? "Customer" : "Supplier"} Outstanding Report (Broker Wise)`
      : `${isCustomerReport(reportType) ? "Customer" : "Supplier"} Outstanding Report`;
    styleRow(r2, C.titleBg, C.titleFont, true, null, totalCols);
    r2.getCell(1).font = { name: "Calibri", size: 11, bold: true, color: { argb: C.titleFont } };
    r2.getCell(1).alignment = { vertical: "middle", horizontal: "center" };

    // ── Row 3: As On Date ────────────────────────────────────
    worksheet.mergeCells(3, 1, 3, totalCols);
    const r3 = worksheet.getRow(3);
    r3.height = 18;
    r3.getCell(1).value = `Outstanding as on ${tillDate}`;
    styleRow(r3, C.dateBg, C.dateFont, false, null, totalCols);
    r3.getCell(1).font = { name: "Calibri", size: 10, italic: true, color: { argb: C.dateFont } };
    r3.getCell(1).alignment = { vertical: "middle", horizontal: "center" };

    // ── Row 4: Column Headers ────────────────────────────────
    const hdrRow = worksheet.getRow(4);
    hdrRow.height = 22;
    colDefs.forEach((col, i) => {
      const cell = hdrRow.getCell(i + 1);
      cell.value = col.header;
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.colHeaderBg } };
      cell.font = { name: "Calibri", size: 10, bold: true, color: { argb: C.colHeaderFont } };
      cell.border = makeBorder();
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    });

    worksheet.views = [{ state: "frozen", ySplit: 4, showGridLines: false }];

    let currentRow = 5;
    let rowCounter = 0; // for alternating row colors

    // ========== HELPER: Write one sale row ==========
    const writeSaleRows = (salesList) => {
      salesList.forEach((sale) => {
        const row = worksheet.getRow(currentRow);
        row.height = 18;

        if (sale?.monthComplete) {
          // ── Month Total row ──
          row.getCell(1).value = "";
          row.getCell(2).value = sale?.monthDisplayText || "Month Total";
          row.getCell(3).value = getSafeNumber(sale?.MonthWiseTotalAmount);
          row.getCell(4).value = getSafeNumber(sale?.MonthWiseReceivedAmount);
          row.getCell(5).value = getSafeNumber(sale?.MonthWiseDiscount);
          row.getCell(6).value = getSafeNumber(sale?.MonthWiseSrPrAmount);
          let co = 6;
          if (extraColumns.showPaymentDetails) {
            co = 9;
          }
          row.getCell(co + 1).value = getSafeNumber(sale?.MonthWiseBalanceAmount);
          styleRow(row, C.monthBg, C.monthFont, true, numFmt, totalCols);
          row.getCell(2).alignment = { horizontal: "left", vertical: "middle" };
        } else if (isAdvanceRow(sale, reportType)) {
          // ── Advance row ──
          row.getCell(1).value = sale?.invoiceNo || "ADVANCE";
          row.getCell(2).value = formatDateDisplay(sale?.date || sale?.invoiceDate);
          row.getCell(3).value = "";
          row.getCell(4).value = getSafeNumber(calcReceivedAmount(sale, reportType));
          row.getCell(5).value = getSafeNumber(calcDiscountAmount(sale));
          row.getCell(6).value = getSafeNumber(calcSrPrAmount(sale, reportType));
          let co = 6;
          if (extraColumns.showPaymentDetails) {
            co = 9;
          }
          row.getCell(co + 1).value = getSafeNumber(calcBalanceAmount(sale, reportType));
          styleRow(row, C.advanceBg, C.advanceFont, false, numFmt, totalCols);
          row.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
          row.getCell(2).alignment = { horizontal: "center", vertical: "middle" };
        } else {
          // ── Normal sale row (alternating) ──
          const bg = rowCounter % 2 === 0 ? C.oddBg : C.evenBg;
          rowCounter++;
          row.getCell(1).value = sale?.invoiceNo || "";
          row.getCell(2).value = formatDateDisplay(sale?.type ? sale?.date : sale?.invoiceDate);
          row.getCell(3).value = getSafeNumber(sale?.totalAmount);
          row.getCell(4).value = getSafeNumber(calcReceivedAmount(sale, reportType));
          row.getCell(5).value = getSafeNumber(calcDiscountAmount(sale));
          row.getCell(6).value = getSafeNumber(calcSrPrAmount(sale, reportType));
          let co = 6;
          if (extraColumns.showPaymentDetails) {
            row.getCell(7).value = sale?.accountData?.[0]?.voucherNo || "";
            row.getCell(8).value = formatDateDisplay(sale?.accountData?.[0]?.voucherDate);
            row.getCell(9).value = getSafeNumber(sale?.accountData?.[0]?.amount);
            co = 9;
          }
          row.getCell(co + 1).value = getSafeNumber(calcBalanceAmount(sale, reportType));
          row.getCell(co + 2).value = formatDateDisplay(
            moment(sale?.invoiceDate).add(getSafeNumber(sale?.creditDays), "days")
          );
          const odd = calcOddDays(sale);
          const showOdd = filterValues.IgnoreCrDays
            ? odd
            : odd !== "" && odd > 0
              ? odd
              : "";
          row.getCell(co + 3).value = showOdd;
          if (extraColumns.showCategory) row.getCell(co + 4).value = sale?.ProdCategoryName || "";
          if (extraColumns.showPoNumber)
            row.getCell(totalCols).value =
              parseData(sale?.purchaseOrderData)
                ?.map((e) => e?.purchaseNo)
                .join(", ") || "";

          styleRow(row, bg, C.normalFont, false, numFmt, totalCols);
          row.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
          row.getCell(2).alignment = { horizontal: "center", vertical: "middle" };
          if (extraColumns.showPaymentDetails) {
            row.getCell(7).alignment = { horizontal: "left", vertical: "middle" };
            row.getCell(8).alignment = { horizontal: "center", vertical: "middle" };
          }
          row.getCell(co + 2).alignment = { horizontal: "center", vertical: "middle" };
          row.getCell(co + 3).alignment = { horizontal: "center", vertical: "middle" };
          // right-align numeric cols
          for (let c = 3; c <= co + 1; c++) {
            row.getCell(c).alignment = { horizontal: "right", vertical: "middle" };
          }
        }

        currentRow++;
      });
    };

    // ========== HELPER: Party Total row ==========
    const writePartyTotal = (salesList) => {
      if (!salesList?.length) return;
      const ptRow = worksheet.getRow(currentRow);
      ptRow.height = 20;
      ptRow.getCell(1).value = "";
      ptRow.getCell(2).value = "Party Total";
      ptRow.getCell(3).value = sumPartyField(salesList, calcAmount, reportType);
      ptRow.getCell(4).value = sumPartyField(salesList, calcReceivedAmount, reportType);
      ptRow.getCell(5).value = sumPartyField(salesList, calcDiscountAmount, reportType);
      ptRow.getCell(6).value = sumPartyField(salesList, calcSrPrAmount, reportType);
      let co = 6;
      if (extraColumns.showPaymentDetails) {
        ptRow.getCell(9).value = sumPartyField(salesList, calcVoucherAmount, reportType);
        co = 9;
      }

      let ptBalance = sumPartyField(salesList, calcBalanceAmount, reportType);
      const ptAdvance = salesList.reduce((acc, s) => {
        if (isAdvanceRow(s, reportType)) return acc + getSafeNumber(s.amount);
        return acc;
      }, 0);
      ptRow.getCell(co + 1).value = ptBalance - ptAdvance;

      styleRow(ptRow, C.partyTotalBg, C.partyTotalFont, true, numFmt, totalCols);
      ptRow.getCell(2).alignment = { horizontal: "left", vertical: "middle" };
      for (let c = 3; c <= co + 1; c++) {
        ptRow.getCell(c).alignment = { horizontal: "right", vertical: "middle" };
      }
      currentRow++;
    };

    // ========== BROKER WISE ==========
    if (isBrokerWise(reportType)) {
      filterReportData.forEach((brokerRow) => {
        // Broker Name Row
        rowCounter = 0;
        const bRow = worksheet.getRow(currentRow);
        bRow.height = 22;
        worksheet.mergeCells(currentRow, 1, currentRow, totalCols);
        bRow.getCell(1).value = brokerRow?.brokerName || "";
        styleRow(bRow, C.brokerBg, C.brokerFont, true, null, totalCols);
        bRow.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
        currentRow++;

        brokerRow?.salesData?.forEach((party) => {
          // Party Name Row
          rowCounter = 0;
          const pRow = worksheet.getRow(currentRow);
          pRow.height = 20;
          worksheet.mergeCells(currentRow, 1, currentRow, totalCols);
          let partyNameDisplay = party?.partyName || "";
          if (watch("printTel") && party) {
            const email = party?.email || "";
            const mobile = party?.mobileNumber || "";
            if (email || mobile) {
              partyNameDisplay += ` (${[email, mobile].filter(Boolean).join(" - ")})`;
            }
          }
          pRow.getCell(1).value = partyNameDisplay;
          styleRow(pRow, C.partyBg, C.partyFont, true, null, totalCols);
          pRow.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
          currentRow++;

          const salesList = party?.salesDetails || [];
          writeSaleRows(salesList);
          writePartyTotal(salesList);
        });
      });

      // ========== NORMAL CUSTOMER / SUPPLIER ==========
    } else {
      filterReportData.forEach((party) => {
        rowCounter = 0;
        const pRow = worksheet.getRow(currentRow);
        pRow.height = 20;
        worksheet.mergeCells(currentRow, 1, currentRow, totalCols);
        let partyNameDisplay = party?.accountName || party?.partyName || "";
        if (watch("printTel") && party) {
          const email = party?.email || "";
          const mobile = party?.mobileNumber || "";
          if (email || mobile) {
            partyNameDisplay += ` (${[email, mobile].filter(Boolean).join(" - ")})`;
          }
        }
        pRow.getCell(1).value = partyNameDisplay;
        styleRow(pRow, C.partyBg, C.partyFont, true, null, totalCols);
        pRow.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
        currentRow++;

        const salesList = party?.salesData || [];
        writeSaleRows(salesList);
        writePartyTotal(salesList);
      });
    }

    // ========== GRAND TOTAL ==========
    if (filterReportData?.length > 0) {
      const sumFn = isBrokerWise(reportType) ? sumBrokerGrandTotalField : sumGrandTotalField;
      const gRow = worksheet.getRow(currentRow);
      gRow.height = 24;
      gRow.getCell(1).value = "";
      gRow.getCell(2).value = "GRAND TOTAL";
      gRow.getCell(3).value = sumFn(filterReportData, calcAmount, reportType);
      gRow.getCell(4).value = sumFn(filterReportData, calcReceivedAmount, reportType);
      gRow.getCell(5).value = sumFn(filterReportData, calcDiscountAmount, reportType);
      gRow.getCell(6).value = sumFn(filterReportData, calcSrPrAmount, reportType);
      let co = 6;
      if (extraColumns.showPaymentDetails) {
        gRow.getCell(9).value = sumFn(filterReportData, calcVoucherAmount, reportType);
        co = 9;
      }

      let gBalance = sumFn(filterReportData, calcBalanceAmount, reportType);

      const gAdvanceTotal = filterReportData.reduce((acc, item) => {
        const salesList = isBrokerWise(reportType)
          ? item?.salesData?.reduce((pAcc, p) => pAcc.concat(p?.salesDetails || []), [])
          : item?.salesData || [];
        return (
          acc +
          salesList.reduce((sAcc, s) => {
            if (isAdvanceRow(s, reportType)) return sAcc + getSafeNumber(s.amount);
            return sAcc;
          }, 0)
        );
      }, 0);

      gRow.getCell(co + 1).value = gBalance - gAdvanceTotal;

      styleRow(gRow, C.grandBg, C.grandFont, true, numFmt, totalCols);
      gRow.getCell(2).alignment = { horizontal: "left", vertical: "middle" };
      gRow.getCell(1).alignment = { horizontal: "left", vertical: "middle" };
      for (let c = 3; c <= co + 1; c++) {
        gRow.getCell(c).alignment = { horizontal: "right", vertical: "middle" };
      }
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${reportFileName}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getCompanyAddress = () => {
    try {
      getWebViewCompanyAddressApiHandler(companyId).then(async (data) => {
        if (data.status == 201 || data.status == 200) {
          setCompanyAddress(data?.data?.[0]);
        } else {
          dispatch({ type: "ADMIN_ERROR_HANDLER", payload: data });
        }
      });
    } catch (error) {
    }
  };

  const getAreaWise = () => {
    // dispatch({ type: "LOADER_START" });
    try {
      getWebViewAeriaWiseFilterOptionsApiHandler(
        companyId,
        watch("outStandingReport")
      ).then(async (data) => {
        // dispatch({ type: "LOADER_START" });
        if (data.status == 201 || data.status == 200) {
          setAreaWise(data?.data?.area);
          setCityWise(data?.data?.city);
          setStateWise(data?.data?.state);
          setGroupWise(data?.data?.group);
          setBroker(data?.data?.brokers);
          let newGroup = data?.data?.sundryDebtors?.map((ele) => {
            return {
              Challan: ele?.Challan ? parseData(ele?.Challan) : {},
              TaxData: ele?.TaxData ? parseData(ele?.TaxData) : {},
              accountName: ele?.accountName,
              brokerId: ele?.brokerId,
              creditDays: ele?.creditDays,
              discount: ele?.discount,
              id: ele?.id,
              partyAddress: ele?.partyAddress
                ? parseData(ele?.partyAddress)
                : "",
              salesAccId: ele?.salesAccId,
              salesType: ele?.salesType,
              subBroker: ele?.subBroker,
              taxCategoryId: ele?.taxCategoryId,
              taxCategoryName: ele?.taxCategoryName,
              transport: ele?.transport,
              transportId: ele?.transportId,
            };
          });
          let newGroupSuppliers = data?.data?.sundryCreditors?.map((ele) => {
            return {
              Challan: ele?.Challan ? parseData(ele?.Challan) : {},
              TaxData: ele?.TaxData ? parseData(ele?.TaxData) : {},
              accountName: ele?.accountName,
              brokerId: ele?.brokerId,
              creditDays: ele?.creditDays,
              discount: ele?.discount,
              id: ele?.id,
              partyAddress: ele?.partyAddress
                ? parseData(ele?.partyAddress)
                : "",
              salesAccId: ele?.salesAccId,
              salesType: ele?.salesType,
              subBroker: ele?.subBroker,
              taxCategoryId: ele?.taxCategoryId,
              taxCategoryName: ele?.taxCategoryName,
              transport: ele?.transport,
              transportId: ele?.transportId,
            };
          });
          setPartyWiseCustomer(newGroup);
          setPartySupplierWiseCustomer(newGroupSuppliers);
          dispatch({ type: "LOADER_END" });
        } else {
          dispatch({ type: "ADMIN_ERROR_HANDLER", payload: data });
        }
      });
    } catch (error) {
    }
  };

  // const submit = async (value) => {
  //   setIdLoading(true);
  //   // create header for range wise
  //   if (
  //     watch("outStandingReport") == "customeragewise" ||
  //     watch("outStandingReport") == "supplieragewise"
  //   ) {
  //     const values = [
  //       parseInt(value["firstPeriod"] || 0, 10),
  //       parseInt(value["secondPeriod"] || 0, 10),
  //       parseInt(value["thirdPeriod"] || 0, 10),
  //       parseInt(value["fourthPeriod"] || 0, 10),
  //     ];
  //     const sortedValues = values.sort((a, b) => a - b);
  //     // Generate ranges
  //     const ranges = sortedValues.map((value, index, arr) => {
  //       const start = index === 0 ? 0 : arr[index - 1] + 1;
  //       return {
  //         headerValue: `${start}-${value}`,
  //         min: start,
  //         max: value,
  //       };
  //     });
  //     ranges.push(`>${sortedValues[sortedValues.length - 1]}`);
  //     setHeaders(ranges);
  //   }

  //   const firstStringDate = dateHeader[0]?.stringDate;
  //   const lastStringDate = dateHeader[dateHeader.length - 1]?.stringDate;

  //   const selectedParties = value?.selectCustomerWise?.length
  //     ? value?.selectCustomerWise
  //     : value?.selectSupplierWise?.length
  //     ? value?.selectSupplierWise
  //     : [] || [];

  //   let partyObject = {};
  //   selectedParties?.forEach((party, index) => {
  //     partyObject[`party[${index}]`] = party;
  //   });

  //   // callApi
  //   let data = {
  //     reportType: value?.outStandingReport,
  //     preferenceType: value?.preference,
  //     locationType: value?.location,
  //     groupType: value?.group_wise,
  //     customerType: value?.customer,
  //     group: value?.select_group,
  //     area: value?.selectAreaWise,
  //     city: value?.selectCityWise,
  //     // party: value?.selectCustomerWise ? value?.selectCustomerWise : value?.selectSupplierWise,
  //     state: value?.selectStateWise,
  //     billType: value?.show,
  //     broker: value?.broker,
  //     brokerType: value?.brokerType,
  //     companyId: companyId,
  //     fromDate:
  //       watch("outStandingReport") == "customermonthwise" ||
  //       watch("outStandingReport") == "suppliermonthwise"
  //         ? formatDate(firstStringDate)
  //         : formatDate(value?.startDate, "submit"),
  //     toDate:
  //       watch("outStandingReport") == "customermonthwise" ||
  //       watch("outStandingReport") == "suppliermonthwise"
  //         ? formatDate(lastStringDate)
  //         : formatDate(value?.endDate, "submit"),
  //     ...partyObject,
  //   };
  //   setSaveButtonLoader(true);
  //   try {
  //     postWebViewOutstandingMasterReportDataApiHandler(data).then((data) => {
  //       if (data?.status == 200) {
  //         setSaveButtonLoader(false);
  //         setValue("isSubmitted", 1);
  //         if (
  //           watch("outStandingReport") == "customer" ||
  //           watch("outStandingReport") == "supplier" ||
  //           watch("outStandingReport") == "customeragewise" ||
  //           watch("outStandingReport") == "supplieragewise"
  //         ) {
  //           let newData = data?.data?.[0]
  //             ?.map((res) => {
  //               let result = {};
  //               if (res?.accountName) result.accountName = res.accountName;
  //               if (res?.brokerId) result.brokerId = res.brokerId;
  //               if (res?.partyId) result.partyId = res.partyId;
  //               if (res?.creditDays) result.creditDays = res.creditDays;
  //               if (res?.returnData)
  //                 result.returnData = parseData(res.returnData);
  //               if (res?.partyAddress)
  //                 result.partyAddress = parseData(res.partyAddress);
  //               const salesData = res?.salesData
  //                 ? parseData(res.salesData)
  //                 : [];
  //               const openingData = res?.openingData
  //                 ? parseData(res.openingData)
  //                 : [];
  //               const advanceData = res?.advanceData
  //                 ? parseData(res.advanceData).map((data) => ({
  //                     ...data,
  //                     balanceAmount: 0,
  //                   }))
  //                 : [];
  //               result.salesData = [
  //                 ...openingData,
  //                 ...salesData,
  //                 ...advanceData,
  //               ].sort((a, b) => {
  //                 const isAEnd =
  //                   a.status === "advance" ||
  //                   (a.balType === "openingBalance" && a.type === "C");

  //                 const isBEnd =
  //                   b.status === "advance" ||
  //                   (b.balType === "openingBalance" && b.type === "C");

  //                 if (isAEnd && !isBEnd) return 1; // a should come after b
  //                 if (!isAEnd && isBEnd) return -1; // a should come before b

  //                 // If both are in same category (either both are "end" or both are not), compare by date
  //                 const dateA = moment(
  //                   a.type ? a.date : a.invoiceDate,
  //                   "YYYY-MM-DD"
  //                 );
  //                 const dateB = moment(
  //                   b.type ? b.date : b.invoiceDate,
  //                   "YYYY-MM-DD"
  //                 );
  //                 return dateA - dateB;
  //               });

  //               return result;
  //             })
  //             ?.filter((item) => item?.salesData && item?.salesData.length > 0);
  //           setReportData(data?.data?.[0] ? newData : []);
  //           setFilterReportData(data?.data?.[0] ? newData : []);
  //         } else if (
  //           watch("outStandingReport") == "customermonthwise" ||
  //           watch("outStandingReport") == "suppliermonthwise"
  //         ) {
  //           let newData = data?.data?.[0]
  //             ?.map((res) => {
  //               let result = {};
  //               if (res?.accountName) result.accountName = res.accountName;
  //               if (res?.brokerId) result.brokerId = res.brokerId;
  //               if (res?.partyId) result.partyId = res.partyId;
  //               if (res?.creditDays) result.creditDays = res.creditDays;
  //               if (res?.salesData) result.salesData = parseData(res.salesData);
  //               if (res?.advanceData)
  //                 result.advanceData = parseData(res.advanceData);
  //               return result;
  //             })
  //             ?.filter((item) => item?.salesData && item?.salesData.length > 0);
  //           setReportData(data?.data?.[0] ? newData : []);
  //           setFilterReportData(data?.data?.[0] ? newData : []);
  //         } else if (
  //           watch("outStandingReport") == "customerbroker" ||
  //           watch("outStandingReport") == "supplierbroker"
  //         ) {
  //           let newData = data?.data?.[0]
  //             ?.map((res) => {
  //               let result = {};
  //               if (res?.brokerAddress)
  //                 result.brokerAddress = parseData(res.brokerAddress);
  //               if (res?.brokerId) result.brokerId = res.brokerId;
  //               if (res?.brokerName) result.brokerName = res.brokerName;
  //               if (res?.brokerSalesData)
  //                 result.salesData = parseData(res.brokerSalesData);
  //               return result;
  //             })
  //             ?.filter((item) => item?.salesData && item?.salesData.length > 0);
  //           setReportData(data?.data?.[0] ? newData : []);
  //           setFilterReportData(data?.data?.[0] ? newData : []);
  //         }
  //         // setFormattedReportData(formattedRecord);
  //       } else {
  //         dispatch({ type: "ADMIN_ERROR_HANDLER", payload: data });
  //         setSaveButtonLoader(false);
  //       }
  //     });
  //   } catch (error) {
  //     setSaveButtonLoader(false);
  //     setIdLoading(false);
  //   }
  // };

  const submit = async (value) => {
    setIdLoading(true);
    // create header for range wise
    if (
      watch("outStandingReport") == "customeragewise" ||
      watch("outStandingReport") == "supplieragewise"
    ) {
      const values = [
        parseInt(value["firstPeriod"] || 0, 10),
        parseInt(value["secondPeriod"] || 0, 10),
        parseInt(value["thirdPeriod"] || 0, 10),
        parseInt(value["fourthPeriod"] || 0, 10),
      ];
      const sortedValues = values.sort((a, b) => a - b);
      // Generate ranges
      const ranges = sortedValues.map((value, index, arr) => {
        const start = index === 0 ? 0 : arr[index - 1] + 1;
        return {
          headerValue: `${start}-${value}`,
          min: start,
          max: value,
        };
      });
      ranges.push(`>${sortedValues[sortedValues.length - 1]}`);
      setHeaders(ranges);
    }

    const firstStringDate = dateHeader[0]?.stringDate;
    const lastStringDate = dateHeader[dateHeader.length - 1]?.stringDate;

    const selectedParties = value?.selectCustomerWise?.length
      ? value?.selectCustomerWise
      : value?.selectSupplierWise?.length
        ? value?.selectSupplierWise
        : [] || [];

    let partyObject = {};
    selectedParties?.forEach((party, index) => {
      partyObject[`party[${index}]`] = party;
    });

    // callApi
    let data = {
      reportType: value?.outStandingReport,
      preferenceType: value?.preference,
      locationType: value?.location,
      groupType: value?.group_wise,
      customerType: value?.customer,
      group: formatArrayForApi(value?.select_group),
      area: formatArrayForApi(value?.selectAreaWise),
      city: formatArrayForApi(value?.selectCityWise),
      state: formatArrayForApi(value?.selectStateWise),
      billType: value?.show,
      broker: value?.broker,
      brokerType: value?.brokerType,
      tillDate: value?.till?.format("YYYY-MM-DD") || "",
      companyId: companyId,
      fromDate:
        watch("outStandingReport") == "customermonthwise" ||
          watch("outStandingReport") == "suppliermonthwise"
          ? formatDate(firstStringDate)
          : formatDate(selectedYearStart),
      toDate:
        watch("outStandingReport") == "customermonthwise" ||
          watch("outStandingReport") == "suppliermonthwise"
          ? formatDate(lastStringDate)
          : formatDate(selectedYearEnd),
      ...partyObject,
    };
    setSaveButtonLoader(true);
    try {
      postWebViewOutstandingMasterReportDataApiHandler(
        data
      ).then((data) => {
        if (data?.status === 404) {
          setSaveButtonLoader(false);
          setIdLoading(false);
          setReportData([]);
          setFilterReportData([]);
          toast.error(data?.message || "Data not found");
        } else if (data?.status == 200) {
          setSaveButtonLoader(false);
          setValue("isSubmitted", 1);

          const reportType = watch("outStandingReport");

          const processData = (res) => {
            if (!res?.salesData && !res?.brokerSalesData && !res?.openingData) return null;

            const salesData = parseData(res.salesData);
            const openingData = parseData(res.openingData);
            const advanceData = value.WithAdvance
              ? parseData(res.advanceData)?.map((d) => ({ ...d, balanceAmount: 0 })) || []
              : [];

            const mergedData = [...openingData, ...salesData, ...advanceData];
            const dataMap = new Map();

            mergedData.forEach((item) => {
              const key = item?.id || item?.invoiceNo;
              if (!dataMap.has(key)) {
                dataMap.set(key, { ...item });
              } else if (item?.ProdCategoryName) {
                let existingCategory = dataMap.get(key)?.ProdCategoryName;
                const categories = [existingCategory, item.ProdCategoryName]
                  .filter((val) => val && val !== "null")
                  .join(", ")
                  .split(", ")
                  .filter((v, i, arr) => arr.indexOf(v) === i);
                dataMap.get(key).ProdCategoryName = categories.join(", ");
              }
            });

            return {
              accountName: res.accountName,
              brokerId: res.brokerId,
              partyId: res.partyId,
              creditDays: res.creditDays,
              returnData: parseData(res.returnData),
              email: res.email,
              mobileNumber: res.mobileNumber,
              partyAddress: parseData(res.partyAddress),
              salesData: sortSalesData(Array.from(dataMap.values()), reportType),
            };
          };

          if (
            reportType == "customer" ||
            reportType == "supplier" ||
            isAgeWise(reportType)
          ) {
            const newData = data?.data?.[0]
              ?.map(processData)
              ?.filter(Boolean)
              ?.filter((item) => item?.salesData?.length > 0)
              ?.sort((a, b) => (a?.accountName || "").localeCompare(b?.accountName || ""));
            setFilterReportData(newData || []);
            setReportData(newData || []);
          } else if (isMonthWise(reportType)) {
            const newData = data?.data?.[0]
              ?.map((res) => ({
                accountName: res.accountName,
                brokerId: res.brokerId,
                partyId: res.partyId,
                creditDays: res.creditDays,
                salesData: parseData(res.salesData),
                advanceData: parseData(res.advanceData),
              }))
              ?.filter((item) => item?.salesData?.length > 0)
              ?.sort((a, b) => (a?.brokerName || "").localeCompare(b?.brokerName || ""));
            setReportData(newData || []);
            setFilterReportData(newData || []);
          } else if (isBrokerWise(reportType)) {
            const newData = data?.data?.[0]
              ?.map((res) => ({
                brokerAddress: parseData(res.brokerAddress),
                brokerId: res.brokerId,
                brokerName: res.brokerName,
                salesData: parseData(res.brokerSalesData),
              }))
              ?.filter((item) => item?.salesData?.length > 0)
              ?.sort((a, b) => (a?.brokerName || "").localeCompare(b?.brokerName || ""));
            setReportData(newData || []);
            setFilterReportData(newData || []);
          }
          setIdLoading(false);
        } else {
          dispatch({ type: "ADMIN_ERROR_HANDLER", payload: data });
          setSaveButtonLoader(false);
          setIdLoading(false);
        }
      });
    } catch (error) {
      setSaveButtonLoader(false);
      setIdLoading(false);
    }
  };

  //get dynamic financial month
  useEffect(() => {
    if (
      watch("outStandingReport") == "customermonthwise" ||
      watch("outStandingReport") == "suppliermonthwise"
    ) {
      const currentDate = new Date();
      const financialYearStart =
        currentDate.getMonth() + 1 < 4
          ? new Date(currentDate.getFullYear() - 1, 3, 1)
          : new Date(currentDate.getFullYear(), 3, 1);

      // Generate the months dynamically
      const headers = [];
      let current = new Date(financialYearStart);

      while (current <= currentDate) {
        let date = {
          date:
            current.toLocaleString("default", { month: "short" }) +
            "-" +
            current.getFullYear(),
          stringDate: `${current.getFullYear()}-${String(
            current.getMonth() + 1
          ).padStart(2, "0")}-01`,
          matchDate: `${current.toLocaleString("default", {
            month: "short",
          })}-${String(current.getFullYear()).slice(-2)}`,
          monthName: current.toLocaleString("default", { month: "long" }),
        };
        headers.push(date);
        // Increment by one month
        current.setMonth(current.getMonth() + 1);
      }
      setDateHeader(headers);
    }
  }, [watch("outStandingReport")]);

  // filter by partyWiseCustomer
  useEffect(() => {
    const locationFilter = (parties, field, value) => {
      if (!value || !Array.isArray(value) || value?.length === 0)
        return parties;
      return parties?.filter((ele) => {
        const fieldValue = ele?.partyAddress?.[0]?.[field];
        return value?.includes(fieldValue);
      });
    };

    const customerFilter = (parties) => {
      if (!watch("preference") || watch("preference") === "All")
        return parties;
      if (watch("selectAreaWise")?.length > 0)
        return locationFilter(parties, "area", watch("selectAreaWise"));
      if (watch("selectCityWise")?.length > 0)
        return locationFilter(parties, "city", watch("selectCityWise"));
      if (watch("selectStateWise")?.length > 0)
        return locationFilter(parties, "stateId", watch("selectStateWise"));
      return parties;
    };

    setFilterWiseCustomerParty(customerFilter(partyWiseCustomer));
    setFilterWiseSupplierParty(customerFilter(partySupplierWiseCustomer));
  }, [
    watch("selectStateWise"),
    watch("selectAreaWise"),
    watch("selectCityWise"),
    watch("preference"),
    partyWiseCustomer,
    partySupplierWiseCustomer,
  ]);

  useEffect(() => {
    getCompanyAddress();
  }, []);

  useEffect(() => {
    getAreaWise();
  }, [watch("outStandingReport")]);

  // merged advanceData, salesDetails and openingData for broker-wise reports
  useEffect(() => {
    if (!isBrokerWise(watch("outStandingReport")) || !reportData?.length) {
      setBrokerReportData([]);
      return;
    }
    const reportType = watch("outStandingReport");
    const withAdvance = watch("WithAdvance");

    const merged = reportData.map((brokerData) => ({
      brokerId: brokerData.brokerId,
      brokerName: brokerData.brokerName,
      salesData: brokerData?.salesData?.map((sale) => ({
        party: sale.party,
        partyName: sale.partyName,
        email: sale.email,
        mobileNumber: sale.mobileNumber,
        salesDetails: sortSalesData([
          ...(sale.salesDetails || []),
          ...(withAdvance ? sale.advanceData || [] : []),
          ...(sale.openingData || []),
        ], reportType),
      })),
    }));

    setBrokerReportData(merged);
    setFilterReportData(merged);
  }, [reportData, watch("outStandingReport"), watch("WithAdvance"), sortSalesData]);

  // filter data based on between/fromDay/toDay, IgnoreCrDays, monthWiseTotal and WithAdvance
  useEffect(() => {
    const { reportType, monthWiseTotal, ...filterVals } = filterValues;
    const withAdvance = watch("WithAdvance");

    if (reportType === "customer" || reportType === "supplier") {
      const filtered = reportData
        ?.map((party) => {
          const salesData = party?.salesData || [];
          const filteredSales = salesData.filter((sale) => {
            if (sale?.status === "advance" && !withAdvance) return false;
            return checkFilterConditions(sale, filterVals);
          });

          if (monthWiseTotal && filteredSales.length > 0) {
            const monthGroups = filteredSales.reduce((acc, sale) => {
              if (!sale?.invoiceDate) return acc;
              const key = moment(sale?.invoiceDate).format("YYYY-MM");
              if (!acc[key]) acc[key] = [];
              acc[key].push(sale);
              return acc;
            }, {});
            const sortedMonthKeys = Object.keys(monthGroups).sort();
            const withMonthTotals = sortedMonthKeys.flatMap((monthKey) => {
              const group = monthGroups[monthKey];
              const monthSummary = createMonthWiseSummary(group, monthKey, reportType);
              return [...group, ...(monthSummary ? [monthSummary] : [])];
            });
            return { ...party, salesData: withMonthTotals };
          }
          return { ...party, salesData: sortSalesData(filteredSales, reportType) };
        })
        ?.filter((party) => {
          const hasActualData = party?.salesData?.some((sale) => !sale?.monthComplete);
          return party?.salesData?.length > 0 && hasActualData;
        });
      setFilterReportData(filtered || []);
    }
    else if (reportType === "customerbroker" || reportType === "supplierbroker") {
      const filtered = brokerReportData
        ?.map((broker) => ({
          ...broker,
          salesData: broker?.salesData
            ?.map((party) => {
              const salesDetails = party?.salesDetails || [];
              const filteredDetails = salesDetails.filter((detail) => {
                if (detail?.status === "advance" && !withAdvance) return false;
                return checkFilterConditions(detail, filterVals);
              });

              if (monthWiseTotal && filteredDetails.length > 0) {
                const monthGroups = filteredDetails.reduce((acc, detail) => {
                  if (!detail?.invoiceDate) return acc;
                  const key = moment(detail?.invoiceDate).format("YYYY-MM");
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(detail);
                  return acc;
                }, {});
                const sortedMonthKeys = Object.keys(monthGroups).sort();
                const withMonthTotals = sortedMonthKeys.flatMap((monthKey) => {
                  const group = monthGroups[monthKey];
                  const monthSummary = createMonthWiseSummary(group, monthKey, reportType);
                  return [...group, ...(monthSummary ? [monthSummary] : [])];
                });
                return { ...party, salesDetails: withMonthTotals };
              }
              return { ...party, salesDetails: sortSalesData(filteredDetails, reportType) };
            })
            ?.filter((party) => {
              const hasActualData = party?.salesDetails?.some((d) => !d?.monthComplete);
              return party?.salesDetails?.length > 0 && hasActualData;
            }) || [],
        }))
        ?.filter((broker) => broker?.salesData?.length > 0);
      setFilterReportData(filtered || []);
    }
    else if (isMonthWise(reportType)) {
      setFilterReportData(reportData);
    }
  }, [reportData, brokerReportData, filterValues, watch("WithAdvance"), checkFilterConditions, sortSalesData]);

  // --- Ignore Cr Days auto-set based on Show selection ---
  useEffect(() => {
    const showType = watch("show");
    if (showType && showType !== "outstanding") {
      setValue("IgnoreCrDays", true);
    } else if (showType === "outstanding") {
      setValue("IgnoreCrDays", false);
    }
  }, [watch("show"), setValue]);

  // show error
  useEffect(() => {
    if (errors?.AgeWiseAnalysis?.message) {
      toast.error(errors?.AgeWiseAnalysis?.message);
    }
  }, [errors?.AgeWiseAnalysis]);

  const generateAndSavePDF = async (printIframe) => {
    const element = printIframe.contentWindow.document.body;

    const canvas = await html2canvas(element, {
      scale: 2,
    });
    const imgData = canvas.toDataURL("image/jpeg", 0.8);

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const imgProps = pdf.getImageProperties(imgData);
    const imgWidth = imgProps.width * 0.264583;
    const imgHeight = imgProps.height * 0.264583;

    const ratio = imgWidth / pdfWidth;
    const adjustedHeight = imgHeight / ratio;

    if (adjustedHeight <= pdfHeight) {
      pdf.addImage(imgData, "jpeg", 0, 0, pdfWidth, adjustedHeight, "", "FAST");
    } else {
      let position = 0;
      while (position < imgHeight) {
        pdf.addImage(
          imgData,
          "PNG",
          0,
          -position / ratio,
          pdfWidth,
          imgHeight / ratio
        );
        position += pdfHeight * ratio;
        if (position < imgHeight) {
          pdf.addPage();
        }
      }
    }

    const pdfBlob = pdf.output("blob", {
      compress: true,
      fontSize: 12,
      quality: "high",
    });

    const formData = new FormData();
    formData.append("profile", pdfBlob, "generated.pdf");

    try {
      uploadPdfApiHandler(formData).then((response) => {
        setIdLoading(false);
        if (response.status == 201 || response.status == 200) {
          // window.open(
          //   "http://localhost:3011/api/uploads/profile-1720590930769.pdf"
          // );
          const link = document.createElement("a");
          link.href = response?.data;
          link.download = "report.pdf"; // Specify the file name
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setIdLoading(false);
        }
      });
    } catch (e) {
      setIdLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    print: async (printIframe) => {
      await generateAndSavePDF(printIframe);
    },
  });

  // Automatically trigger print when data changes and is not empty
  useEffect(() => {
    if (filterReportData?.length > 0) {
      handlePrint();
    }
  }, [filterReportData]);

  const renderPartyTotalRow = useCallback((salesData, reportType, extraColumns = {}) => {
    const safeSalesData = salesData || [];
    const amount = sumPartyField(safeSalesData, calcAmount, reportType);
    const received = sumPartyField(safeSalesData, calcReceivedAmount, reportType);
    const discount = sumPartyField(safeSalesData, calcDiscountAmount, reportType);
    const srPr = sumPartyField(safeSalesData, calcSrPrAmount, reportType);
    const voucher = sumPartyField(safeSalesData, calcVoucherAmount, reportType);

    let balance = sumPartyField(safeSalesData, calcBalanceAmount, reportType);

    const totalAdvanceAmount = safeSalesData.reduce((acc, sale) => {
      if (isAdvanceRow(sale, reportType)) {
        return acc + getSafeNumber(sale?.amount || sale?.totalAmount);
      }
      return acc;
    }, 0);

    balance = balance - totalAdvanceAmount;

    return (
      <TableRow className="table-row">
        <TableCell component="th" className="table-th"></TableCell>
        <TableCell component="th" className="table-th">Party Total</TableCell>
        <TableCell component="th" className="table-th">{formatNumber(amount)}</TableCell>
        <TableCell component="th" className="table-th">{formatNumber(received)}</TableCell>
        <TableCell component="th" className="table-th text-align-right">{formatNumber(discount)}</TableCell>
        <TableCell component="th" className="table-th text-align-right">{formatNumber(srPr)}</TableCell>
        {extraColumns.showPaymentDetails && (
          <>
            <TableCell component="th" className="table-th"></TableCell>
            <TableCell component="th" className="table-th"></TableCell>
            <TableCell component="th" className="table-th">{formatNumber(voucher)}</TableCell>
          </>
        )}
        <TableCell component="th" className="table-th">{formatNumber(balance)}</TableCell>
        <TableCell component="th" className="table-th text-align-right"></TableCell>
        <TableCell component="th" className="table-th text-align-right"></TableCell>
        {extraColumns.showCategory && <TableCell component="td" className="table-td text-align-right"></TableCell>}
        {extraColumns.showPoNumber && <TableCell component="td" className="table-td text-align-right"></TableCell>}
      </TableRow>
    );
  }, []);

  const renderGrandTotalRow = useCallback((reportType, extraColumns = {}) => {
    if (!filterReportData?.length) {
      return (
        <TableRow className="table-row">
          <TableCell component="td" className="table-td text-center" colSpan="100%">
            No Data Found
          </TableCell>
        </TableRow>
      );
    }

    const isBroker = isBrokerWise(reportType);
    const sumFn = isBroker ? sumBrokerGrandTotalField : sumGrandTotalField;

    const amount = sumFn(filterReportData, calcAmount, reportType);
    const received = sumFn(filterReportData, calcReceivedAmount, reportType);
    const discount = sumFn(filterReportData, calcDiscountAmount, reportType);
    const srPr = sumFn(filterReportData, calcSrPrAmount, reportType);
    const voucher = sumFn(filterReportData, calcVoucherAmount, reportType);

    let balance = sumFn(filterReportData, calcBalanceAmount, reportType);

    const calculateTotalAdvance = (data) => {
      return data.reduce((acc, item) => {
        const salesList = isBroker
          ? item?.salesData?.reduce((pAcc, p) => pAcc.concat(p?.salesDetails || []), [])
          : item?.salesData || [];

        return acc + salesList.reduce((sAcc, sale) => {
          if (isAdvanceRow(sale, reportType)) {
            return sAcc + getSafeNumber(sale?.amount || sale?.totalAmount);
          }
          return sAcc;
        }, 0);
      }, 0);
    };

    const grandTotalAdvance = calculateTotalAdvance(filterReportData);
    balance = balance - grandTotalAdvance;

    return (
      <TableRow className="table-row table-row-hilight">
        <TableCell component="th" className="table-th"></TableCell>
        <TableCell component="th" className="table-th">Grand Total</TableCell>
        <TableCell component="th" className="table-th">{formatNumber(amount)}</TableCell>
        <TableCell component="th" className="table-th">{formatNumber(received)}</TableCell>
        <TableCell component="th" className="table-th text-align-right">{formatNumber(discount)}</TableCell>
        <TableCell component="th" className="table-th text-align-right">{formatNumber(srPr)}</TableCell>
        {extraColumns.showPaymentDetails && (
          <>
            <TableCell component="th" className="table-th"></TableCell>
            <TableCell component="th" className="table-th"></TableCell>
            <TableCell component="th" className="table-th">{formatNumber(voucher)}</TableCell>
          </>
        )}
        <TableCell component="th" className="table-th">{formatNumber(balance)}</TableCell>
        <TableCell component="th" className="table-th"></TableCell>
        <TableCell component="th" className="table-th text-align-right"></TableCell>
        {extraColumns.showCategory && <TableCell component="td" className="table-td text-align-right">{"-"}</TableCell>}
        {extraColumns.showPoNumber && <TableCell component="td" className="table-td text-align-right">{"-"}</TableCell>}
      </TableRow>
    );
  }, [filterReportData]);

  const reportContent = () => {
    const extraColumns = {
      showPaymentDetails: watch("printWithPaymentDetails"),
      showCategory: watch("category"),
      showPoNumber: watch("poNumber"),
    };

    if (
      watch("outStandingReport") == "customer" ||
      watch("outStandingReport") == "supplier"
    ) {
      return (
        <Index.Box className="card-spacing" style={{ display: "none" }}>
          <Index.Box className="page-table-main web-view-outstanding-report-table-main">
            <TableContainer
              component={Paper}
              className="table-container summary-table-container"
            >
              <div ref={printRef}>
                <Table
                  sx={{ minWidth: 650 }}
                  aria-label="simple table"
                  className="table"
                >
                  <TableHead className="table-head">
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-th"
                        colSpan="100%"
                      >
                        <Index.Box className="table-summary-bg">
                          <Index.Typography
                            variant="h4"
                            component="h4"
                            className="view-titles"
                          >
                            {watch("outStandingReport") == "customer"
                              ? "Customer"
                              : "Supplier"}{" "}
                            Outstanding Report
                          </Index.Typography>
                          <Index.Typography
                            variant="h5"
                            component="h5"
                            className="view-subtitle"
                          ></Index.Typography>
                        </Index.Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody className="table-body">
                    {filterReportData?.map((row, index) => {
                      return (
                        <>
                          {watch("printOnLetter") && (
                            <>
                              <TableRow className="table-row">
                                <TableCell
                                  component="th"
                                  scope="row"
                                  className="table-th"
                                  colSpan={"100%"}
                                >
                                  <Index.Typography className="table-data-text">
                                    {companyAddress?.Company_name}
                                  </Index.Typography>
                                </TableCell>
                              </TableRow>

                              <TableRow className="table-row">
                                <TableCell
                                  component="td"
                                  scope="row"
                                  className="table-td"
                                  colSpan={"100%"}
                                >
                                  {companyAddress?.o_add1
                                    ? `${companyAddress?.o_add1},`
                                    : ""}
                                  {companyAddress?.o_add2
                                    ? `${companyAddress?.o_add2},`
                                    : ""}
                                  {companyAddress?.o_add3
                                    ? `${companyAddress?.o_add3},`
                                    : ""}
                                </TableCell>
                              </TableRow>
                              <TableRow className="table-row">
                                <TableCell
                                  component="td"
                                  scope="row"
                                  className="table-td"
                                  colSpan={"100%"}
                                >
                                  {companyAddress?.cityName
                                    ? `${companyAddress?.cityName},`
                                    : ""}
                                  {companyAddress?.Statename
                                    ? `${companyAddress?.Statename}-`
                                    : ""}
                                  {companyAddress?.o_pin
                                    ? `${companyAddress?.o_pin}`
                                    : ""}
                                </TableCell>
                              </TableRow>

                              <TableRow className="table-row">
                                <TableCell
                                  component="th"
                                  scope="row"
                                  className="table-th"
                                  colSpan={"100%"}
                                >
                                  To
                                </TableCell>
                              </TableRow>
                            </>
                          )}

                          <TableRow className="table-row">
                            <TableCell
                              component="th"
                              scope="row"
                              className="table-th"
                              colSpan={"100%"}
                            >
                              {row?.accountName}
                              {watch("printTel") && row?.partyAddress?.[0] && (
                                <span style={{ fontWeight: "normal", marginLeft: "5px" }}>
                                  {row?.email && row?.email !== "0" && row?.email !== 0 && row?.mobileNumber
                                    ? `(${row.email}, ${row.mobileNumber})`
                                    : row?.email && row?.email !== "0" && row?.email !== 0
                                      ? `(${row.email})`
                                      : row?.mobileNumber
                                        ? `(${row.mobileNumber})`
                                        : ""}
                                </span>
                              )}
                            </TableCell>
                          </TableRow>

                          {watch("printOnLetter") && (
                            <>
                              <TableRow className="table-row">
                                <TableCell
                                  component="td"
                                  scope="row"
                                  className="table-td"
                                  colSpan={"100%"}
                                >
                                  {row?.partyAddress?.[0]?.address1
                                    ? row?.partyAddress?.[0]?.address1
                                    : ""}
                                  {row?.partyAddress?.[0]?.address2
                                    ? row?.partyAddress?.[0]?.address2
                                    : ""}
                                  {row?.partyAddress?.[0]?.address3
                                    ? row?.partyAddress?.[0]?.address3
                                    : ""}
                                </TableCell>
                              </TableRow>
                              <TableRow className="table-row">
                                <TableCell
                                  component="td"
                                  scope="row"
                                  className="table-td"
                                  colSpan={"100%"}
                                >
                                  {row?.partyAddress?.[0]?.areaName
                                    ? row?.partyAddress?.[0]?.areaName
                                    : ""}
                                  {row?.partyAddress?.[0]?.cityName
                                    ? `${row.partyAddress[0].cityName},`
                                    : ""}
                                  {row?.partyAddress?.[0]?.Statename
                                    ? `${row.partyAddress[0].Statename}-`
                                    : ""}
                                  {row?.partyAddress?.[0]?.pin
                                    ? `${row.partyAddress[0].pin}`
                                    : ""}
                                </TableCell>
                              </TableRow>
                              <TableRow className="table-row">
                                <TableCell
                                  component="td"
                                  scope="row"
                                  className="table-td"
                                  colSpan={"100%"}
                                >
                                  Kindly check below out standing bill early as
                                  possible
                                </TableCell>
                              </TableRow>
                              <TableRow className="table-row">
                                <TableCell
                                  component="td"
                                  scope="row"
                                  className="table-td"
                                >
                                  Details of outstanding as on
                                </TableCell>
                                <TableCell
                                  component="td"
                                  scope="row"
                                  className="table-td"
                                  colSpan={"100%"}
                                >
                                  {watch("latterDate")
                                    ? formatDate(watch("latterDate"), "print")
                                    : moment().format("DD-MM-YYYY")}
                                  {/* { moment(watch("latterDate")).format("DD-MM-YYYY")} */}
                                </TableCell>
                              </TableRow>
                            </>
                          )}
                          <TableRow className="table-row">
                            <TableCell
                              component="th"
                              scope="row"
                              className="table-th text-align-right"
                            >
                              Bill No.
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              className="table-th text-align-right"
                            >
                              Bill Date
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              className="table-th text-align-right"
                            >
                              Amount
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              className="table-th text-align-right"
                            >
                              Received Amount
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              className="table-th text-align-right"
                            >
                              Discount Amount
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              className="table-th text-align-right"
                            >
                              {watch("outStandingReport") == "customer"
                                ? "SR Amount"
                                : "PR Amount"}
                            </TableCell>
                            {watch("printWithPaymentDetails") && (
                              <>
                                <TableCell
                                  component="th"
                                  scope="row"
                                  className="table-th text-align-right"
                                >
                                  Voucher No.
                                </TableCell>
                                <TableCell
                                  component="th"
                                  scope="row"
                                  className="table-th text-align-right"
                                >
                                  Voucher Date
                                </TableCell>
                                <TableCell
                                  component="th"
                                  scope="row"
                                  className="table-th text-align-right"
                                >
                                  Voucher Amount
                                </TableCell>
                              </>
                            )}
                            <TableCell
                              component="th"
                              scope="row"
                              className="table-th text-align-right"
                            >
                              Balance Amount
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              className="table-th text-align-right"
                            >
                              Due Date
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              className="table-th text-align-right"
                            >
                              O/D Days
                            </TableCell>
                            {watch("category") && (
                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                Category
                              </TableCell>
                            )}
                            {watch("poNumber") && (
                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                P.O Number
                              </TableCell>
                            )}
                          </TableRow>
                          {row?.salesData?.length > 0 ? (
                            row.salesData
                              .slice()
                              // .sort((a, b) => {
                              //   const dateA = new Date(a.date || a.invoiceDate);
                              //   const dateB = new Date(b.date || b.invoiceDate);
                              //   return dateA - dateB;
                              // })
                              .map((salesData, index) => {
                                return (
                                  <>
                                    {!salesData?.monthComplete ? (
                                      // <TableRow className={`table-row ${salesData?.type == "C" || salesData?.transactionType ? "table-highligt-row" : salesData?.type == "D" ? "table-highligt-row-red" : ""}`}>
                                      <TableRow
                                        className={`table-row ${(salesData?.status == "advance" ||
                                          (salesData?.balType ==
                                            "openingBalance" &&
                                            salesData?.type == "C")) &&
                                          "table-highligt-row-red"
                                          }`}
                                      >
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        >
                                          {salesData?.invoiceNo}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        >
                                          {/* {moment(salesData?.invoiceDate).format("DD-MM-YYYY")} */}
                                          {moment(
                                            salesData?.type
                                              ? salesData?.date
                                              : salesData?.invoiceDate
                                          ).format("DD-MM-YYYY")}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        >
                                          {/* {salesData?.totalAmount?.toFixed(2)} */}
                                          {salesData?.balType ==
                                            "openingBalance" &&
                                            salesData?.type == "C"
                                            ? ""
                                            : salesData?.totalAmount?.toFixed(
                                              2
                                            )}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        >
                                          {formatNumber(
                                            calcReceivedAmount(
                                              salesData,
                                              watch("outStandingReport")
                                            )
                                          )}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td text-align-right"
                                        >
                                          {Number(
                                            salesData?.accountData?.reduce(
                                              (acc, account) => {
                                                const amount =
                                                  account?.discount || 0;
                                                if (
                                                  account?.salesReference ==
                                                  "cashBank"
                                                ) {
                                                  return (
                                                    acc +
                                                    (isNaN(amount) ? 0 : amount)
                                                  );
                                                } else {
                                                  return acc;
                                                }
                                              },
                                              0
                                            ) || 0
                                          ).toFixed(2)}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td text-align-right"
                                        >
                                          {salesData?.balType !=
                                            "openingBalance"
                                            ? Number(
                                              salesData?.accountData?.reduce(
                                                (acc, account) => {
                                                  const amount =
                                                    account?.amount || 0;
                                                  if (
                                                    account?.salesReference ==
                                                    "salesReturn" ||
                                                    account?.salesReference ==
                                                    "" ||
                                                    account?.salesReference ==
                                                    "creditNote" ||
                                                    account?.salesReference ==
                                                    "purchaseReturn"
                                                  ) {
                                                    return (
                                                      acc +
                                                      (isNaN(amount)
                                                        ? 0
                                                        : amount)
                                                    );
                                                  } else {
                                                    return acc;
                                                  }
                                                },
                                                0
                                              ) || 0
                                            ).toFixed(2)
                                            : "0.00"}
                                        </TableCell>

                                        {watch("printWithPaymentDetails") && (
                                          <>
                                            <TableCell
                                              component="td"
                                              scope="row"
                                              className="table-td text-align-right"
                                            >
                                              {
                                                salesData?.accountData?.[0]
                                                  ?.voucherNo
                                              }
                                            </TableCell>

                                            <TableCell
                                              component="td"
                                              scope="row"
                                              className="table-td text-align-right"
                                            >
                                              {salesData?.accountData?.[0]
                                                ?.voucherDate
                                                ? moment(
                                                  salesData?.accountData?.[0]
                                                    ?.voucherDate
                                                ).format("DD-MM-YYYY")
                                                : ""}
                                            </TableCell>

                                            <TableCell
                                              component="td"
                                              scope="row"
                                              className="table-td"
                                            >
                                              {salesData?.accountData?.[0]?.amount?.toFixed(
                                                2
                                              ) || "0.00"}
                                            </TableCell>
                                          </>
                                        )}
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        >
                                          {salesData?.salesReturnRemainAmount
                                            ? salesData?.salesReturnRemainAmount?.toFixed(
                                              2
                                            )
                                            : salesData?.status == "advance" ||
                                              (salesData?.balType ==
                                                "openingBalance" &&
                                                salesData?.type == "C")
                                              ? 0
                                              : Math.abs(
                                                salesData?.amount || 0
                                              )?.toFixed(2)}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td text-align-right"
                                        >
                                          {moment(salesData?.invoiceDate)
                                            .add(
                                              Number(salesData?.creditDays),
                                              "days"
                                            )
                                            .format("DD-MM-YYYY")}
                                        </TableCell>
                                        <TableCell component="td" scope="row" className="table-td text-align-right">
                                          {(() => {
                                            const odd = calcOddDays(salesData);
                                            return filterValues.ignoreCrDays
                                              ? odd
                                              : odd !== "" && odd > 0
                                                ? odd
                                                : "";
                                          })()}
                                        </TableCell>
                                        {watch("category") && (
                                          <TableCell
                                            component="td"
                                            scope="row"
                                            className="table-td text-align-right"
                                          >
                                            {salesData?.ProdCategoryName}
                                          </TableCell>
                                        )}
                                        {watch("poNumber") && (
                                          <TableCell
                                            component="td"
                                            scope="row"
                                            className="table-td text-align-right"
                                          >
                                            {salesData?.purchaseOrderData &&
                                              Array.isArray(
                                                parseData(
                                                  salesData?.purchaseOrderData
                                                )
                                              ) && (
                                                <div>
                                                  {parseData(
                                                    salesData?.purchaseOrderData
                                                  )
                                                    .map(
                                                      (ele) => ele?.purchaseNo
                                                    )
                                                    .join(", ")}
                                                </div>
                                              )}
                                          </TableCell>
                                        )}
                                      </TableRow>
                                    ) : watch("monthWiseTotal") ? (
                                      <TableRow className="table-row">
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        ></TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        >
                                          {salesData?.monthDisplayText || "Month Total"}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        >
                                          {salesData?.MonthWiseTotalAmount?.toFixed(
                                            2
                                          )}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        >
                                          {salesData?.MonthWiseReceivedAmount?.toFixed(2)}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td text-align-right"
                                        >
                                          {salesData?.MonthWiseDiscount?.toFixed(
                                            2
                                          )}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td text-align-right"
                                        >
                                          {salesData?.MonthWiseSrPrAmount?.toFixed(
                                            2
                                          )}
                                        </TableCell>

                                        {watch("printWithPaymentDetails") && (
                                          <>
                                            <TableCell
                                              component="td"
                                              scope="row"
                                              className="table-td text-align-right"
                                            >
                                              {/* {salesData?.accountData?.[0]?.voucherNo} */}
                                            </TableCell>

                                            <TableCell
                                              component="td"
                                              scope="row"
                                              className="table-td text-align-right"
                                            >
                                              {/* {salesData?.accountData?.[0]?.voucherDate} */}
                                            </TableCell>

                                            <TableCell
                                              component="td"
                                              scope="row"
                                              className="table-td"
                                            >
                                              {/* {salesData?.accountData?.[0]?.amount?.toFixed(2) || "0.00"} */}
                                            </TableCell>
                                          </>
                                        )}
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        >
                                          {salesData?.MonthWiseBalanceAmount?.toFixed(
                                            2
                                          )}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td text-align-right"
                                        >
                                          {" "}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td text-align-right"
                                        ></TableCell>
                                        {watch("category") && (
                                          <TableCell
                                            component="td"
                                            scope="row"
                                            className="table-td text-align-right"
                                          >
                                            {/* {salesData?.ProdCategoryName} */}
                                          </TableCell>
                                        )}
                                        {watch("poNumber") && (
                                          <TableCell
                                            component="td"
                                            scope="row"
                                            className="table-td text-align-right"
                                          >
                                            {/* {salesData?.ProdCategoryName} */}
                                          </TableCell>
                                        )}
                                      </TableRow>
                                    ) : null}

                                    {watch("printWithPaymentDetails") && (
                                      <>
                                        {salesData?.accountData
                                          ?.slice(1)
                                          ?.map((accountData, index) => {
                                            return (
                                              <>
                                                <TableRow className="table-row">
                                                  <TableCell
                                                    component="td"
                                                    scope="row"
                                                    className="table-td"
                                                  ></TableCell>
                                                  <TableCell
                                                    component="td"
                                                    scope="row"
                                                    className="table-td"
                                                  ></TableCell>
                                                  <TableCell
                                                    component="td"
                                                    scope="row"
                                                    className="table-td"
                                                  ></TableCell>
                                                  <TableCell
                                                    component="td"
                                                    scope="row"
                                                    className="table-td"
                                                  ></TableCell>
                                                  <TableCell
                                                    component="td"
                                                    scope="row"
                                                    className="table-td text-align-right"
                                                  ></TableCell>
                                                  <TableCell
                                                    component="td"
                                                    scope="row"
                                                    className="table-td text-align-right"
                                                  ></TableCell>
                                                  {watch(
                                                    "printWithPaymentDetails"
                                                  ) && (
                                                      <>
                                                        <TableCell
                                                          component="td"
                                                          scope="row"
                                                          className="table-td text-align-right"
                                                        >
                                                          {accountData?.voucherNo}
                                                        </TableCell>

                                                        <TableCell
                                                          component="td"
                                                          scope="row"
                                                          className="table-td text-align-right"
                                                        >
                                                          {accountData?.voucherDate
                                                            ? moment(
                                                              accountData?.voucherDate
                                                            ).format(
                                                              "DD-MM-YYYY"
                                                            )
                                                            : ""}
                                                        </TableCell>

                                                        <TableCell
                                                          component="td"
                                                          scope="row"
                                                          className="table-td"
                                                        >
                                                          {accountData?.amount?.toFixed(
                                                            2
                                                          )}
                                                        </TableCell>
                                                      </>
                                                    )}

                                                  <TableCell
                                                    component="td"
                                                    scope="row"
                                                    className="table-td"
                                                  >
                                                    {
                                                      //accountData?.balanceAmt?.toFixed(2)
                                                    }
                                                  </TableCell>
                                                  <TableCell
                                                    component="td"
                                                    scope="row"
                                                    className="table-td text-align-right"
                                                  ></TableCell>
                                                  <TableCell
                                                    component="td"
                                                    scope="row"
                                                    className="table-td text-align-right"
                                                  ></TableCell>

                                                  {watch("category") && (
                                                    <TableCell
                                                      component="td"
                                                      scope="row"
                                                      className="table-td text-align-right"
                                                    >
                                                      {" "}
                                                    </TableCell>
                                                  )}
                                                  {watch("poNumber") && (
                                                    <TableCell
                                                      component="td"
                                                      scope="row"
                                                      className="table-td text-align-right"
                                                    >
                                                      {" "}
                                                    </TableCell>
                                                  )}
                                                </TableRow>
                                              </>
                                            );
                                          })}
                                      </>
                                    )}
                                  </>
                                );
                              })
                          ) : (
                            <TableRow className="table-row">
                              <TableCell
                                component="td"
                                scope="row"
                                className="table-td text-center"
                                colSpan={"100%"}
                              >
                                No Data Found
                              </TableCell>
                            </TableRow>
                          )}

                          {row?.salesData?.length > 0 &&
                            renderPartyTotalRow(row?.salesData, watch("outStandingReport"), extraColumns)}

                          {row?.salesData?.length > 0 &&
                            watch("printOnLetter") && (
                              <>
                                <TableRow className="table-row">
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th text-align-right"
                                    colSpan={"100%"}
                                  >
                                    cc.note
                                  </TableCell>
                                </TableRow>

                                <TableRow className="table-row">
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                    colSpan={"100%"}
                                  ></TableCell>
                                </TableRow>
                                <TableRow className="table-row">
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                    colSpan={"100%"}
                                  ></TableCell>
                                </TableRow>

                                <TableRow className="table-row">
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th text-align-right"
                                    colSpan={"100%"}
                                  >
                                    for {`${row?.accountName}`}
                                  </TableCell>
                                </TableRow>

                                <TableRow className="table-row">
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                    colSpan={"100%"}
                                  ></TableCell>
                                </TableRow>
                                <TableRow className="table-row">
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                    colSpan={"100%"}
                                  ></TableCell>
                                </TableRow>

                                <TableRow className="table-row">
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th text-align-right"
                                    colSpan={"100%"}
                                  >
                                    Authorised Signatory
                                  </TableCell>
                                </TableRow>

                                <TableRow className="table-row">
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                  ></TableCell>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                  ></TableCell>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                  ></TableCell>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                  ></TableCell>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                  ></TableCell>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                  ></TableCell>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                  ></TableCell>
                                </TableRow>
                              </>
                            )}
                        </>
                      );
                    })}

                    {renderGrandTotalRow(watch("outStandingReport"), extraColumns)}
                  </TableBody>
                </Table>
              </div>
            </TableContainer>
          </Index.Box>
        </Index.Box>
      );
    } else if (
      watch("outStandingReport") == "customerbroker" ||
      watch("outStandingReport") == "supplierbroker"
    ) {
      return (
        <Index.Box className="card-spacing" style={{ display: "none" }}>
          <Index.Box className="page-table-main web-view-outstanding-report-table-main">
            <TableContainer
              component={Paper}
              className="table-container summary-table-container"
            >
              <div ref={printRef}>
                <Table
                  sx={{ minWidth: 650 }}
                  aria-label="simple table"
                  className="table"
                >
                  <TableHead className="table-head">
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-th"
                        colSpan="100%"
                      >
                        <Index.Box className="table-summary-bg">
                          <Index.Typography
                            variant="h4"
                            component="h4"
                            className="view-titles"
                          >
                            {watch("outStandingReport") == "customer"
                              ? "Customer"
                              : "Supplier"}{" "}
                            Outstanding Report (BrokerWise)
                          </Index.Typography>
                          <Index.Typography
                            variant="h5"
                            component="h5"
                            className="view-subtitle"
                          ></Index.Typography>
                        </Index.Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody className="table-body">
                    {filterReportData?.map((mainRow, index) => {
                      return (
                        <>
                          <TableRow className="table-row">
                            <TableCell
                              component="th"
                              scope="row"
                              className="table-th"
                              colSpan={"100%"}
                            >
                              {mainRow?.brokerName}
                            </TableCell>

                            {watch("ageSummary") && (
                              <TableRow className="table-row">
                                <TableCell
                                  component="th"
                                  scope="row"
                                  className="table-th text-align-right"
                                >
                                  Bill No.
                                </TableCell>
                                <TableCell
                                  component="th"
                                  scope="row"
                                  className="table-th text-align-right"
                                >
                                  Bill Date
                                </TableCell>
                                <TableCell
                                  component="th"
                                  scope="row"
                                  className="table-th text-align-right"
                                >
                                  Amount
                                </TableCell>
                                <TableCell
                                  component="th"
                                  scope="row"
                                  className="table-th text-align-right"
                                >
                                  Received Amount
                                </TableCell>
                                <TableCell
                                  component="th"
                                  scope="row"
                                  className="table-th text-align-right"
                                >
                                  Discount Amount
                                </TableCell>
                                <TableCell
                                  component="th"
                                  scope="row"
                                  className="table-th text-align-right"
                                >
                                  SR Amount
                                </TableCell>
                                {watch("printWithPaymentDetails") && (
                                  <>
                                    <TableCell
                                      component="th"
                                      scope="row"
                                      className="table-th text-align-right"
                                    >
                                      Voucher No.
                                    </TableCell>
                                    <TableCell
                                      component="th"
                                      scope="row"
                                      className="table-th text-align-right"
                                    >
                                      Voucher Date
                                    </TableCell>
                                    <TableCell
                                      component="th"
                                      scope="row"
                                      className="table-th text-align-right"
                                    >
                                      Voucher Amount
                                    </TableCell>
                                  </>
                                )}
                                <TableCell
                                  component="th"
                                  scope="row"
                                  className="table-th text-align-right"
                                >
                                  Balance Amount
                                </TableCell>
                                <TableCell
                                  component="th"
                                  scope="row"
                                  className="table-th text-align-right"
                                >
                                  Due Date
                                </TableCell>
                                <TableCell
                                  component="th"
                                  scope="row"
                                  className="table-th text-align-right"
                                >
                                  O/D Days
                                </TableCell>
                                {watch("category") && (
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th text-align-right"
                                  >
                                    Category
                                  </TableCell>
                                )}
                              </TableRow>
                            )}
                          </TableRow>
                          {mainRow?.salesData?.map((row, index) => {
                            let salesData = row?.salesDetails;
                            return (
                              <>
                                {watch("ageSummary") == false && (
                                  <>
                                    <TableRow className="table-row">
                                      <TableCell
                                        component="th"
                                        scope="row"
                                        className="table-th"
                                        colSpan={"100%"}
                                      >
                                        {row?.partyName}
                                        {watch("printTel") && row && (
                                          <span style={{ fontWeight: "normal", marginLeft: "5px" }}>
                                            {row?.email && row?.email !== "0" && row?.email !== 0 && row?.mobileNumber
                                              ? `(${row.email}, ${row.mobileNumber})`
                                              : row?.email && row?.email !== "0" && row?.email !== 0
                                                ? `(${row.email})`
                                                : row?.mobileNumber
                                                  ? `(${row.mobileNumber})`
                                                  : ""}
                                          </span>
                                        )}
                                      </TableCell>
                                    </TableRow>

                                    <TableRow className="table-row">
                                      <TableCell
                                        component="th"
                                        scope="row"
                                        className="table-th text-align-right"
                                      >
                                        Bill No.
                                      </TableCell>
                                      <TableCell
                                        component="th"
                                        scope="row"
                                        className="table-th text-align-right"
                                      >
                                        Bill Date
                                      </TableCell>
                                      <TableCell
                                        component="th"
                                        scope="row"
                                        className="table-th text-align-right"
                                      >
                                        Amount
                                      </TableCell>
                                      <TableCell
                                        component="th"
                                        scope="row"
                                        className="table-th text-align-right"
                                      >
                                        Received Amount
                                      </TableCell>
                                      <TableCell
                                        component="th"
                                        scope="row"
                                        className="table-th text-align-right"
                                      >
                                        Discount Amount
                                      </TableCell>
                                      <TableCell
                                        component="th"
                                        scope="row"
                                        className="table-th text-align-right"
                                      >
                                        SR Amount
                                      </TableCell>
                                      {watch("printWithPaymentDetails") && (
                                        <>
                                          <TableCell
                                            component="th"
                                            scope="row"
                                            className="table-th text-align-right"
                                          >
                                            Voucher No.
                                          </TableCell>
                                          <TableCell
                                            component="th"
                                            scope="row"
                                            className="table-th text-align-right"
                                          >
                                            Voucher Date
                                          </TableCell>
                                          <TableCell
                                            component="th"
                                            scope="row"
                                            className="table-th text-align-right"
                                          >
                                            Voucher Amount
                                          </TableCell>
                                        </>
                                      )}
                                      <TableCell
                                        component="th"
                                        scope="row"
                                        className="table-th text-align-right"
                                      >
                                        Balance Amount
                                      </TableCell>
                                      <TableCell
                                        component="th"
                                        scope="row"
                                        className="table-th text-align-right"
                                      >
                                        Due Date
                                      </TableCell>
                                      <TableCell
                                        component="th"
                                        scope="row"
                                        className="table-th text-align-right"
                                      >
                                        O/D Days
                                      </TableCell>
                                      {watch("category") && (
                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th text-align-right"
                                        >
                                          Category
                                        </TableCell>
                                      )}
                                    </TableRow>
                                  </>
                                )}
                                {salesData?.length > 0 ? (
                                  salesData
                                    // .sort((a, b) => {
                                    //   const dateA = new Date(
                                    //     a.date || a.invoiceDate
                                    //   );
                                    //   const dateB = new Date(
                                    //     b.date || b.invoiceDate
                                    //   );
                                    //   return dateA - dateB;
                                    // })
                                    ?.map((salesDataMap, index) => {
                                      let diffDays = salesDataMap?.creditDays
                                        ? moment().diff(
                                          moment(
                                            salesDataMap?.invoiceDate
                                          ).add(
                                            Number(salesDataMap?.creditDays),
                                            "days"
                                          ),
                                          "days"
                                        ) + 1
                                        : moment().diff(
                                          moment(salesDataMap?.invoiceDate),
                                          "days"
                                        ) + 2;
                                      return (
                                        <>
                                          {!salesDataMap?.monthComplete ? (
                                            // <TableRow className={`table-row ${salesDataMap?.type == "C" || salesDataMap?.transactionType ? "table-highligt-row" : salesDataMap?.type == "D" ? "table-highligt-row-red" : ""}`}>
                                            <TableRow
                                              className={`table-row ${(salesDataMap?.status ==
                                                "advance" ||
                                                (salesDataMap?.balType ==
                                                  "openingBalance" &&
                                                  salesDataMap?.type ==
                                                  "C")) &&
                                                "table-highligt-row-red"
                                                }`}
                                            >
                                              <TableCell
                                                component="td"
                                                scope="row"
                                                className="table-td"
                                              >
                                                {salesDataMap?.invoiceNo}
                                              </TableCell>
                                              <TableCell
                                                component="td"
                                                scope="row"
                                                className="table-td"
                                              >
                                                {moment(
                                                  salesDataMap?.invoiceDate
                                                ).format("DD-MM-YYYY")}
                                              </TableCell>
                                              <TableCell
                                                component="td"
                                                scope="row"
                                                className="table-td"
                                              >
                                                {isAdvanceRow(
                                                  salesDataMap,
                                                  watch("outStandingReport")
                                                )
                                                  ? ""
                                                  : formatNumber(
                                                    calcAmount(
                                                      salesDataMap,
                                                      watch("outStandingReport")
                                                    )
                                                  )}
                                              </TableCell>
                                              <TableCell
                                                component="td"
                                                scope="row"
                                                className="table-td"
                                              >
                                                {formatNumber(
                                                  calcReceivedAmount(
                                                    salesDataMap,
                                                    watch("outStandingReport")
                                                  )
                                                )}
                                              </TableCell>
                                              <TableCell
                                                component="td"
                                                scope="row"
                                                className="table-td text-align-right"
                                              >
                                                {formatNumber(
                                                  calcDiscountAmount(
                                                    salesDataMap,
                                                    watch("outStandingReport")
                                                  )
                                                )}
                                              </TableCell>
                                              <TableCell
                                                component="td"
                                                scope="row"
                                                className="table-td text-align-right"
                                              >
                                                {formatNumber(
                                                  calcSrPrAmount(
                                                    salesDataMap,
                                                    watch("outStandingReport")
                                                  )
                                                )}
                                              </TableCell>

                                              {watch(
                                                "printWithPaymentDetails"
                                              ) && (
                                                  <>
                                                    <TableCell
                                                      component="td"
                                                      scope="row"
                                                      className="table-td text-align-right"
                                                    >
                                                      {
                                                        salesDataMap
                                                          ?.accountData?.[0]
                                                          ?.voucherNo
                                                      }
                                                    </TableCell>

                                                    <TableCell
                                                      component="td"
                                                      scope="row"
                                                      className="table-td text-align-right"
                                                    >
                                                      {
                                                        salesDataMap
                                                          ?.accountData?.[0]
                                                          ?.voucherDate
                                                      }
                                                    </TableCell>

                                                    <TableCell
                                                      component="td"
                                                      scope="row"
                                                      className="table-td"
                                                    >
                                                      {salesDataMap?.accountData?.[0]?.amount?.toFixed(
                                                        2
                                                      ) || "0.00"}
                                                    </TableCell>
                                                  </>
                                                )}
                                              <TableCell
                                                component="td"
                                                scope="row"
                                                className="table-td"
                                              >
                                                {salesDataMap?.salesReturnRemainAmount
                                                  ? salesDataMap?.salesReturnRemainAmount?.toFixed(
                                                    2
                                                  )
                                                  : salesDataMap?.status ==
                                                    "advance" ||
                                                    (salesDataMap?.balType ==
                                                      "openingBalance" &&
                                                      salesDataMap?.type == "C")
                                                    ? ""
                                                    : Math.abs(
                                                      salesDataMap?.amount || 0
                                                    )?.toFixed(2)}
                                              </TableCell>
                                              <TableCell
                                                component="td"
                                                scope="row"
                                                className="table-td text-align-right"
                                              >
                                                {moment(
                                                  salesDataMap?.invoiceDate
                                                )
                                                  .add(
                                                    Number(
                                                      salesDataMap?.creditDays
                                                    ),
                                                    "days"
                                                  )
                                                  .format("DD-MM-YYYY")}
                                              </TableCell>
                                              <TableCell
                                                component="td"
                                                scope="row"
                                                className="table-td text-align-right"
                                              >
                                                {(() => {
                                                  const odd = calcOddDays(salesDataMap);
                                                  return watch("IgnoreCrDays")
                                                    ? odd
                                                    : odd !== "" && odd > 0
                                                      ? odd
                                                      : "";
                                                })()}
                                              </TableCell>
                                              {watch("category") && (
                                                <TableCell
                                                  component="td"
                                                  scope="row"
                                                  className="table-td text-align-right"
                                                >
                                                  {
                                                    salesDataMap?.ProdCategoryName
                                                  }
                                                </TableCell>
                                              )}
                                            </TableRow>
                                          ) : watch("monthWiseTotal") ? (
                                            <TableRow className="table-row">
                                              <TableCell
                                                component="td"
                                                scope="row"
                                                className="table-td"
                                              ></TableCell>
                                              <TableCell
                                                component="td"
                                                scope="row"
                                                className="table-td"
                                              >
                                                {salesDataMap?.monthDisplayText || "Month Total"}
                                              </TableCell>
                                              <TableCell
                                                component="td"
                                                scope="row"
                                                className="table-td"
                                              >
                                                {salesDataMap?.MonthWiseTotalAmount?.toFixed(
                                                  2
                                                )}
                                              </TableCell>
                                              <TableCell
                                                component="td"
                                                scope="row"
                                                className="table-td"
                                              >
                                                {salesDataMap?.MonthWiseReceivedAmount?.toFixed(2)}
                                              </TableCell>
                                              <TableCell
                                                component="td"
                                                scope="row"
                                                className="table-td text-align-right"
                                              >
                                                {salesDataMap?.MonthWiseDiscount?.toFixed(
                                                  2
                                                )}
                                              </TableCell>
                                              <TableCell
                                                component="td"
                                                scope="row"
                                                className="table-td text-align-right"
                                              >
                                                {salesDataMap?.MonthWiseSrPrAmount?.toFixed(
                                                  2
                                                )}
                                              </TableCell>

                                              {watch(
                                                "printWithPaymentDetails"
                                              ) && (
                                                  <>
                                                    <TableCell
                                                      component="td"
                                                      scope="row"
                                                      className="table-td text-align-right"
                                                    >
                                                      {/* {salesData?.accountData?.[0]?.voucherNo} */}
                                                    </TableCell>

                                                    <TableCell
                                                      component="td"
                                                      scope="row"
                                                      className="table-td text-align-right"
                                                    >
                                                      {/* {salesData?.accountData?.[0]?.voucherDate} */}
                                                    </TableCell>

                                                    <TableCell
                                                      component="td"
                                                      scope="row"
                                                      className="table-td"
                                                    >
                                                      {/* {salesData?.accountData?.[0]?.amount?.toFixed(2) || "0.00"} */}
                                                    </TableCell>
                                                  </>
                                                )}
                                              <TableCell
                                                component="td"
                                                scope="row"
                                                className="table-td"
                                              >
                                                {salesDataMap?.MonthWiseBalanceAmount?.toFixed(
                                                  2
                                                )}
                                              </TableCell>
                                              <TableCell
                                                component="td"
                                                scope="row"
                                                className="table-td text-align-right"
                                              >
                                                {" "}
                                              </TableCell>
                                              <TableCell
                                                component="td"
                                                scope="row"
                                                className="table-td text-align-right"
                                              ></TableCell>
                                              {watch("category") && (
                                                <TableCell
                                                  component="td"
                                                  scope="row"
                                                  className="table-td text-align-right"
                                                >
                                                  {/* {salesData?.ProdCategoryName} */}
                                                </TableCell>
                                              )}
                                            </TableRow>
                                          ) : null}

                                          {watch("printWithPaymentDetails") && (
                                            <>
                                              {salesDataMap?.accountData
                                                ?.slice(1)
                                                ?.map((accountData, index) => {
                                                  return (
                                                    <>
                                                      <TableRow className="table-row">
                                                        <TableCell
                                                          component="td"
                                                          scope="row"
                                                          className="table-td"
                                                        ></TableCell>
                                                        <TableCell
                                                          component="td"
                                                          scope="row"
                                                          className="table-td"
                                                        ></TableCell>
                                                        <TableCell
                                                          component="td"
                                                          scope="row"
                                                          className="table-td"
                                                        ></TableCell>
                                                        <TableCell
                                                          component="td"
                                                          scope="row"
                                                          className="table-td"
                                                        ></TableCell>
                                                        <TableCell
                                                          component="td"
                                                          scope="row"
                                                          className="table-td text-align-right"
                                                        ></TableCell>
                                                        <TableCell
                                                          component="td"
                                                          scope="row"
                                                          className="table-td text-align-right"
                                                        ></TableCell>
                                                        {watch(
                                                          "printWithPaymentDetails"
                                                        ) && (
                                                            <>
                                                              <TableCell
                                                                component="td"
                                                                scope="row"
                                                                className="table-td text-align-right"
                                                              >
                                                                {
                                                                  accountData?.voucherNo
                                                                }
                                                              </TableCell>

                                                              <TableCell
                                                                component="td"
                                                                scope="row"
                                                                className="table-td text-align-right"
                                                              >
                                                                {
                                                                  accountData?.voucherDate
                                                                }
                                                              </TableCell>

                                                              <TableCell
                                                                component="td"
                                                                scope="row"
                                                                className="table-td"
                                                              >
                                                                {accountData?.amount?.toFixed(
                                                                  2
                                                                )}
                                                              </TableCell>
                                                            </>
                                                          )}

                                                        <TableCell
                                                          component="td"
                                                          scope="row"
                                                          className="table-td"
                                                        >
                                                          {
                                                            //accountData?.balanceAmt?.toFixed(2)
                                                          }
                                                        </TableCell>
                                                        <TableCell
                                                          component="td"
                                                          scope="row"
                                                          className="table-td text-align-right"
                                                        ></TableCell>
                                                        <TableCell
                                                          component="td"
                                                          scope="row"
                                                          className="table-td text-align-right"
                                                        ></TableCell>

                                                        {watch("category") && (
                                                          <TableCell
                                                            component="td"
                                                            scope="row"
                                                            className="table-td text-align-right"
                                                          >
                                                            {" "}
                                                          </TableCell>
                                                        )}
                                                      </TableRow>
                                                    </>
                                                  );
                                                })}
                                            </>
                                          )}
                                        </>
                                      );
                                    })
                                ) : (
                                  <TableRow className="table-row">
                                    <TableCell
                                      component="td"
                                      scope="row"
                                      className="table-td text-center"
                                      colSpan={"100%"}
                                    >
                                      No Data Found
                                    </TableCell>
                                  </TableRow>
                                )}

                                {salesData?.length > 0 &&
                                  renderPartyTotalRow(salesData, watch("outStandingReport"), extraColumns)}

                                {salesData?.length > 0 &&
                                  watch("printOnLetter") && (
                                    <>
                                      <TableRow className="table-row">
                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th text-align-right"
                                          colSpan={"100%"}
                                        >
                                          cc.note
                                        </TableCell>
                                      </TableRow>

                                      <TableRow className="table-row">
                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th"
                                          colSpan={"100%"}
                                        ></TableCell>
                                      </TableRow>
                                      <TableRow className="table-row">
                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th"
                                          colSpan={"100%"}
                                        ></TableCell>
                                      </TableRow>

                                      <TableRow className="table-row">
                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th text-align-right"
                                          colSpan={"100%"}
                                        >
                                          for {`${row?.accountName}`}
                                        </TableCell>
                                      </TableRow>

                                      <TableRow className="table-row">
                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th"
                                          colSpan={"100%"}
                                        ></TableCell>
                                      </TableRow>
                                      <TableRow className="table-row">
                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th"
                                          colSpan={"100%"}
                                        ></TableCell>
                                      </TableRow>

                                      <TableRow className="table-row">
                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th text-align-right"
                                          colSpan={"100%"}
                                        >
                                          Authorised Signatory
                                        </TableCell>
                                      </TableRow>

                                      <TableRow className="table-row">
                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th"
                                        ></TableCell>
                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th"
                                        ></TableCell>
                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th"
                                        ></TableCell>
                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th"
                                        ></TableCell>
                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th"
                                        ></TableCell>
                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th"
                                        ></TableCell>
                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th"
                                        ></TableCell>
                                      </TableRow>
                                    </>
                                  )}
                              </>
                            );
                          })}
                        </>
                      );
                    })}

                    {renderGrandTotalRow(watch("outStandingReport"), extraColumns)}
                  </TableBody>
                </Table>
              </div>
            </TableContainer>
          </Index.Box>
        </Index.Box>
      );
    } else if (
      watch("outStandingReport") == "customeragewise" ||
      watch("outStandingReport") == "supplieragewise"
    ) {
      return (
        <Index.Box className="card-spacing" style={{ display: "none" }}>
          <Index.Box className="page-table-main web-view-outstanding-report-table-main">
            <TableContainer
              component={Paper}
              className="table-container summary-table-container"
            >
              <div ref={printRef}>
                <Table
                  sx={{ minWidth: 650 }}
                  aria-label="simple table"
                  className="table"
                >
                  <TableHead className="table-head">
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-th"
                        colSpan="100%"
                      >
                        <Index.Box className="table-summary-bg">
                          <Index.Typography
                            variant="h4"
                            component="h4"
                            className="view-titles"
                          >
                            {watch("outStandingReport") == "customeragewise"
                              ? "Customer"
                              : "Supplier"}{" "}
                            Outstanding Report (Age wise Analysis)
                          </Index.Typography>
                          <Index.Typography
                            variant="h5"
                            component="h5"
                            className="view-subtitle"
                          ></Index.Typography>
                        </Index.Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody className="table-body">
                    {watch("ageSummary") && (
                      <TableRow className="table-row">
                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th text-align-right"
                        >
                          Party Name
                        </TableCell>

                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th text-align-right"
                        >
                          Amount
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th text-align-right"
                        >
                          Received Amount
                        </TableCell>

                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th text-align-right"
                        >
                          Balance Amount
                        </TableCell>

                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th text-align-right"
                        >
                          {headers[0]?.headerValue}
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th text-align-right"
                        >
                          {headers[1]?.headerValue}
                        </TableCell>

                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th text-align-right"
                        >
                          {headers[2]?.headerValue}
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th text-align-right"
                        >
                          {headers[3]?.headerValue}
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th text-align-right"
                        >
                          {`>${watch("fourthPeriod")}`}
                        </TableCell>
                      </TableRow>
                    )}
                    {filterReportData?.map((row, index) => {
                      return (
                        <>
                          {watch("ageSummary") == false && (
                            <TableRow className="table-row">
                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th"
                                colSpan={"100%"}
                              >
                                {row?.accountName}
                              </TableCell>
                            </TableRow>
                          )}

                          {watch("ageSummary") == false && (
                            <TableRow className="table-row">
                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                Bill No.
                              </TableCell>
                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                Bill Date
                              </TableCell>

                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                Amount
                              </TableCell>
                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                Received Amount
                              </TableCell>

                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                Balance Amount
                              </TableCell>

                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                Due Date
                              </TableCell>
                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                O/D Days
                              </TableCell>

                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                {headers[0]?.headerValue}
                              </TableCell>
                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                {headers[1]?.headerValue}
                              </TableCell>

                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                {headers[2]?.headerValue}
                              </TableCell>
                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                {headers[3]?.headerValue}
                              </TableCell>
                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                {`>${watch("fourthPeriod")}`}
                              </TableCell>
                            </TableRow>
                          )}
                          {watch("ageSummary") == false &&
                            (row?.salesData?.length > 0 ? (
                              row?.salesData?.map((salesData, index) => {
                                let diffOfDays = salesData?.creditDays
                                  ? moment().diff(
                                    moment(salesData?.invoiceDate).add(
                                      Number(salesData?.creditDays),
                                      "days"
                                    ),
                                    "days"
                                  ) + 1
                                  : moment().diff(
                                    moment(salesData?.invoiceDate),
                                    "days"
                                  ) + 1;
                                return (
                                  <>
                                    {!salesData?.monthComplete ? (
                                      <TableRow className="table-row">
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        >
                                          {salesData?.invoiceNo}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        >
                                          {moment(
                                            salesData?.invoiceDate
                                          ).format("DD-MM-YYYY")}
                                        </TableCell>

                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        >
                                          {salesData?.totalAmount?.toFixed(2)}
                                        </TableCell>

                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        >
                                          {(
                                            salesData?.totalAmount -
                                            salesData?.salesReturnRemainAmount
                                          )?.toFixed(2)}
                                        </TableCell>

                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        >
                                          {salesData?.salesReturnRemainAmount?.toFixed(
                                            2
                                          )}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td text-align-right"
                                        >
                                          {moment(salesData?.invoiceDate)
                                            .add(
                                              Number(salesData?.creditDays),
                                              "days"
                                            )
                                            .format("DD-MM-YYYY")}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td text-align-right"
                                        >
                                          {/* {salesData?.creditDays ? moment(salesData?.invoiceDate)
                                                                                                .add(Number(salesData?.creditDays), 'days')
                                                                                                .diff(moment(), 'days') : ""} */}
                                          {salesData?.creditDays
                                            ? moment().diff(
                                              moment(
                                                salesData?.invoiceDate
                                              ).add(
                                                Number(salesData?.creditDays),
                                                "days"
                                              ),
                                              "days"
                                            ) + 1
                                            : moment().diff(
                                              moment(salesData?.invoiceDate),
                                              "days"
                                            ) + 1}
                                        </TableCell>

                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th text-align-right"
                                        >
                                          {headers[0]?.min < diffOfDays &&
                                            headers[0]?.max > diffOfDays
                                            ? salesData?.salesReturnRemainAmount
                                            : null}
                                        </TableCell>

                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th text-align-right"
                                        >
                                          {headers[1]?.min < diffOfDays &&
                                            headers[1]?.max > diffOfDays
                                            ? salesData?.salesReturnRemainAmount
                                            : null}
                                        </TableCell>
                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th text-align-right"
                                        >
                                          {headers[2]?.min < diffOfDays &&
                                            headers[2]?.max > diffOfDays
                                            ? salesData?.salesReturnRemainAmount
                                            : null}
                                        </TableCell>

                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th text-align-right"
                                        >
                                          {headers[3]?.min < diffOfDays &&
                                            headers[3]?.max > diffOfDays
                                            ? salesData?.salesReturnRemainAmount
                                            : null}
                                        </TableCell>

                                        <TableCell
                                          component="th"
                                          scope="row"
                                          className="table-th text-align-right"
                                        >
                                          {watch("fourthPeriod") < diffOfDays
                                            ? salesData?.salesReturnRemainAmount
                                            : null}
                                        </TableCell>
                                      </TableRow>
                                    ) : watch("monthWiseTotal") ? (
                                      <TableRow className="table-row">
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        ></TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        >
                                          {salesData?.monthDisplayText || "Month Total"}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        >
                                          {salesData?.MonthWiseTotalAmount?.toFixed(
                                            2
                                          )}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        >
                                          {salesData?.MonthWiseAmount?.toFixed(
                                            2
                                          )}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td text-align-right"
                                        >
                                          {salesData?.MonthWiseDiscount?.toFixed(
                                            2
                                          )}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td text-align-right"
                                        >
                                          {salesData?.MonthWisesalesReturnAmountData?.toFixed(
                                            2
                                          )}
                                        </TableCell>

                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td"
                                        >
                                          {salesData?.MonthWiseSalesReturnRemainAmount?.toFixed(
                                            2
                                          )}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td text-align-right"
                                        >
                                          {" "}
                                        </TableCell>
                                        <TableCell
                                          component="td"
                                          scope="row"
                                          className="table-td text-align-right"
                                        ></TableCell>
                                      </TableRow>
                                    ) : null}
                                  </>
                                );
                              })
                            ) : (
                              <TableRow className="table-row">
                                <TableCell
                                  component="td"
                                  scope="row"
                                  className="table-td text-center"
                                  colSpan={"100%"}
                                >
                                  No Data Found
                                </TableCell>
                              </TableRow>
                            ))}

                          {row?.salesData?.length > 0 && (
                            <TableRow className="table-row">
                              {watch("ageSummary") == false ? (
                                <>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                  ></TableCell>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                  >
                                    {" "}
                                    Party Total{" "}
                                  </TableCell>
                                </>
                              ) : (
                                <>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                  >
                                    {" "}
                                    {row?.accountName}{" "}
                                  </TableCell>
                                </>
                              )}

                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th"
                              >
                                {Number(
                                  row?.salesData?.reduce((acc, account) => {
                                    return (
                                      (acc || 0) + (account.totalAmount || 0)
                                    );
                                  }, 0)
                                ).toFixed(2)}
                              </TableCell>
                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th"
                              >
                                {(
                                  Number(
                                    row?.salesData?.reduce((acc, account) => {
                                      return (
                                        (acc || 0) + (account.totalAmount || 0)
                                      );
                                    }, 0)
                                  ) -
                                  Number(
                                    row?.salesData?.reduce((acc, account) => {
                                      return (
                                        (acc || 0) +
                                        (account.salesReturnRemainAmount || 0)
                                      );
                                    }, 0)
                                  )
                                )?.toFixed(2)}
                              </TableCell>

                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                {Number(
                                  row?.salesData?.reduce((acc, account) => {
                                    return (
                                      (acc || 0) +
                                      (account.salesReturnRemainAmount || 0)
                                    );
                                  }, 0)
                                ).toFixed(2)}
                              </TableCell>

                              {watch("ageSummary") == false && (
                                <>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th text-align-right"
                                  ></TableCell>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th text-align-right"
                                  ></TableCell>
                                </>
                              )}

                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                {Number(
                                  row?.salesData?.reduce((acc, account) => {
                                    const diffOfDays = account.creditDays
                                      ? moment().diff(
                                        moment(account.invoiceDate).add(
                                          Number(account.creditDays),
                                          "days"
                                        ),
                                        "days"
                                      )
                                      : moment().diff(
                                        moment(account.invoiceDate),
                                        "days"
                                      );

                                    // Check if diffOfDays falls within the first header's range
                                    if (
                                      headers[0]?.min <= diffOfDays &&
                                      diffOfDays <= headers[0]?.max
                                    ) {
                                      return (
                                        acc +
                                        (account.salesReturnRemainAmount || 0)
                                      );
                                    }
                                    return acc;
                                  }, 0)
                                ).toFixed(2)}
                              </TableCell>
                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                {Number(
                                  row?.salesData?.reduce((acc, account) => {
                                    const diffOfDays = account.creditDays
                                      ? moment().diff(
                                        moment(account.invoiceDate).add(
                                          Number(account.creditDays),
                                          "days"
                                        ),
                                        "days"
                                      )
                                      : moment().diff(
                                        moment(account.invoiceDate),
                                        "days"
                                      );

                                    // Check if diffOfDays falls within the first header's range
                                    if (
                                      headers[1]?.min <= diffOfDays &&
                                      diffOfDays <= headers[1]?.max
                                    ) {
                                      return (
                                        acc +
                                        (account.salesReturnRemainAmount || 0)
                                      );
                                    }
                                    return acc;
                                  }, 0)
                                ).toFixed(2)}
                              </TableCell>
                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                {Number(
                                  row?.salesData?.reduce((acc, account) => {
                                    const diffOfDays = account.creditDays
                                      ? moment().diff(
                                        moment(account.invoiceDate).add(
                                          Number(account.creditDays),
                                          "days"
                                        ),
                                        "days"
                                      )
                                      : moment().diff(
                                        moment(account.invoiceDate),
                                        "days"
                                      );

                                    // Check if diffOfDays falls within the first header's range
                                    if (
                                      headers[2]?.min <= diffOfDays &&
                                      diffOfDays <= headers[2]?.max
                                    ) {
                                      return (
                                        acc +
                                        (account.salesReturnRemainAmount || 0)
                                      );
                                    }
                                    return acc;
                                  }, 0)
                                ).toFixed(2)}
                              </TableCell>
                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                {Number(
                                  row?.salesData?.reduce((acc, account) => {
                                    const diffOfDays = account.creditDays
                                      ? moment().diff(
                                        moment(account.invoiceDate).add(
                                          Number(account.creditDays),
                                          "days"
                                        ),
                                        "days"
                                      )
                                      : moment().diff(
                                        moment(account.invoiceDate),
                                        "days"
                                      );

                                    // Check if diffOfDays falls within the first header's range
                                    if (
                                      headers[3]?.min <= diffOfDays &&
                                      diffOfDays <= headers[3]?.max
                                    ) {
                                      return (
                                        acc +
                                        (account.salesReturnRemainAmount || 0)
                                      );
                                    }
                                    return acc;
                                  }, 0)
                                ).toFixed(2)}
                              </TableCell>
                              <TableCell
                                component="th"
                                scope="row"
                                className="table-th text-align-right"
                              >
                                {Number(
                                  row?.salesData?.reduce((acc, account) => {
                                    const diffOfDays = account.creditDays
                                      ? moment().diff(
                                        moment(account.invoiceDate).add(
                                          Number(account.creditDays),
                                          "days"
                                        ),
                                        "days"
                                      )
                                      : moment().diff(
                                        moment(account.invoiceDate),
                                        "days"
                                      );

                                    // Check if diffOfDays is greater than the value from watch("fourthPeriod")
                                    if (watch("fourthPeriod") < diffOfDays) {
                                      return (
                                        acc +
                                        (account.salesReturnRemainAmount || 0)
                                      );
                                    }
                                    return acc;
                                  }, 0)
                                ).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          )}

                          {row?.salesData?.length > 0 &&
                            watch("printOnLetter") && (
                              <>
                                <TableRow className="table-row">
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th text-align-right"
                                    colSpan={"100%"}
                                  >
                                    cc.note
                                  </TableCell>
                                </TableRow>

                                <TableRow className="table-row">
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                    colSpan={"100%"}
                                  ></TableCell>
                                </TableRow>
                                <TableRow className="table-row">
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                    colSpan={"100%"}
                                  ></TableCell>
                                </TableRow>

                                <TableRow className="table-row">
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th text-align-right"
                                    colSpan={"100%"}
                                  >
                                    for {`${row?.accountName}`}
                                  </TableCell>
                                </TableRow>

                                <TableRow className="table-row">
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                    colSpan={"100%"}
                                  ></TableCell>
                                </TableRow>
                                <TableRow className="table-row">
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                    colSpan={"100%"}
                                  ></TableCell>
                                </TableRow>

                                <TableRow className="table-row">
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th text-align-right"
                                    colSpan={"100%"}
                                  >
                                    Authorised Signatory
                                  </TableCell>
                                </TableRow>

                                <TableRow className="table-row">
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                  ></TableCell>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                  ></TableCell>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                  ></TableCell>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                  ></TableCell>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                  ></TableCell>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                  ></TableCell>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    className="table-th"
                                  ></TableCell>
                                </TableRow>
                              </>
                            )}
                        </>
                      );
                    })}

                    {filterReportData?.length > 0 ? (
                      <TableRow className="table-row">
                        {watch("ageSummary") == false ? (
                          <>
                            <TableCell
                              component="th"
                              scope="row"
                              className="table-th"
                            ></TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              className="table-th"
                            >
                              {" "}
                              Grand Total{" "}
                            </TableCell>
                          </>
                        ) : (
                          <TableCell
                            component="th"
                            scope="row"
                            className="table-th"
                          >
                            {" "}
                            Grand Total
                          </TableCell>
                        )}
                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th"
                        >
                          {Number(
                            filterReportData?.reduce(
                              (reportDataSum, report) =>
                                reportDataSum +
                                (report?.salesData?.reduce(
                                  (salesSum, sale) =>
                                    salesSum + (sale?.totalAmount || 0),
                                  0
                                ) || 0),
                              0
                            ) || 0
                          ).toFixed(2)}
                        </TableCell>

                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th"
                        >
                          {(
                            Number(
                              filterReportData?.reduce(
                                (reportDataSum, report) =>
                                  reportDataSum +
                                  (report?.salesData?.reduce(
                                    (salesSum, sale) =>
                                      salesSum + (sale?.totalAmount || 0),
                                    0
                                  ) || 0),
                                0
                              ) || 0
                            ) -
                            Number(
                              filterReportData?.reduce((sum, account) => {
                                return (
                                  (sum || 0) +
                                  account?.salesData?.reduce(
                                    (innerSum, sale) => {
                                      return (
                                        (innerSum || 0) +
                                        (sale.salesReturnRemainAmount || 0)
                                      );
                                    },
                                    0
                                  )
                                );
                              }, 0)
                            )
                          )?.toFixed(2)}
                        </TableCell>

                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th text-align-right"
                        >
                          {Number(
                            filterReportData?.reduce((sum, account) => {
                              return (
                                (sum || 0) +
                                account?.salesData?.reduce((innerSum, sale) => {
                                  return (
                                    (innerSum || 0) +
                                    (sale.salesReturnRemainAmount || 0)
                                  );
                                }, 0)
                              );
                            }, 0)
                          )?.toFixed(2)}
                        </TableCell>

                        {watch("ageSummary") == false && (
                          <>
                            <TableCell
                              component="th"
                              scope="row"
                              className="table-th"
                            ></TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              className="table-th text-align-right"
                            ></TableCell>
                          </>
                        )}

                        {watch("category") && (
                          <TableCell
                            component="td"
                            scope="row"
                            className="table-td text-align-right"
                          >
                            {"-"}
                          </TableCell>
                        )}
                        {watch("poNumber") && (
                          <TableCell
                            component="td"
                            scope="row"
                            className="table-td text-align-right"
                          >
                            {"-"}
                          </TableCell>
                        )}

                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th text-align-right"
                        >
                          {Number(
                            filterReportData?.reduce((sum, account) => {
                              return (
                                sum +
                                account?.salesData?.reduce((innerSum, sale) => {
                                  const diffOfDays = sale.creditDays
                                    ? moment().diff(
                                      moment(sale.invoiceDate).add(
                                        Number(sale.creditDays),
                                        "days"
                                      ),
                                      "days"
                                    )
                                    : moment().diff(
                                      moment(sale.invoiceDate),
                                      "days"
                                    );

                                  // Apply condition based on "watch('fourthPeriod')" and "diffOfDays"
                                  if (
                                    headers[0]?.min <= diffOfDays &&
                                    diffOfDays <= headers[0]?.max
                                  ) {
                                    return (
                                      innerSum +
                                      (sale.salesReturnRemainAmount || 0)
                                    );
                                  }

                                  return innerSum;
                                }, 0)
                              );
                            }, 0)
                          )?.toFixed(2)}
                        </TableCell>

                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th text-align-right"
                        >
                          {Number(
                            filterReportData?.reduce((sum, account) => {
                              return (
                                sum +
                                account?.salesData?.reduce((innerSum, sale) => {
                                  const diffOfDays = sale.creditDays
                                    ? moment().diff(
                                      moment(sale.invoiceDate).add(
                                        Number(sale.creditDays),
                                        "days"
                                      ),
                                      "days"
                                    )
                                    : moment().diff(
                                      moment(sale.invoiceDate),
                                      "days"
                                    );

                                  // Apply condition based on "watch('fourthPeriod')" and "diffOfDays"
                                  if (
                                    headers[1]?.min <= diffOfDays &&
                                    diffOfDays <= headers[1]?.max
                                  ) {
                                    return (
                                      innerSum +
                                      (sale.salesReturnRemainAmount || 0)
                                    );
                                  }

                                  return innerSum;
                                }, 0)
                              );
                            }, 0)
                          )?.toFixed(2)}
                        </TableCell>

                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th text-align-right"
                        >
                          {Number(
                            filterReportData?.reduce((sum, account) => {
                              return (
                                sum +
                                account?.salesData?.reduce((innerSum, sale) => {
                                  const diffOfDays = sale.creditDays
                                    ? moment().diff(
                                      moment(sale.invoiceDate).add(
                                        Number(sale.creditDays),
                                        "days"
                                      ),
                                      "days"
                                    )
                                    : moment().diff(
                                      moment(sale.invoiceDate),
                                      "days"
                                    );

                                  // Apply condition based on "watch('fourthPeriod')" and "diffOfDays"
                                  if (
                                    headers[2]?.min <= diffOfDays &&
                                    diffOfDays <= headers[2]?.max
                                  ) {
                                    return (
                                      innerSum +
                                      (sale.salesReturnRemainAmount || 0)
                                    );
                                  }

                                  return innerSum;
                                }, 0)
                              );
                            }, 0)
                          )?.toFixed(2)}
                        </TableCell>

                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th text-align-right"
                        >
                          {Number(
                            filterReportData?.reduce((sum, account) => {
                              return (
                                sum +
                                account?.salesData?.reduce((innerSum, sale) => {
                                  const diffOfDays = sale.creditDays
                                    ? moment().diff(
                                      moment(sale.invoiceDate).add(
                                        Number(sale.creditDays),
                                        "days"
                                      ),
                                      "days"
                                    )
                                    : moment().diff(
                                      moment(sale.invoiceDate),
                                      "days"
                                    );

                                  // Apply condition based on "watch('fourthPeriod')" and "diffOfDays"
                                  if (
                                    headers[3]?.min <= diffOfDays &&
                                    diffOfDays <= headers[3]?.max
                                  ) {
                                    return (
                                      innerSum +
                                      (sale.salesReturnRemainAmount || 0)
                                    );
                                  }

                                  return innerSum;
                                }, 0)
                              );
                            }, 0)
                          )?.toFixed(2)}
                        </TableCell>

                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th text-align-right"
                        >
                          {Number(
                            filterReportData?.reduce((sum, account) => {
                              return (
                                sum +
                                account?.salesData?.reduce((innerSum, sale) => {
                                  const diffOfDays = sale.creditDays
                                    ? moment().diff(
                                      moment(sale.invoiceDate).add(
                                        Number(sale.creditDays),
                                        "days"
                                      ),
                                      "days"
                                    )
                                    : moment().diff(
                                      moment(sale.invoiceDate),
                                      "days"
                                    );

                                  // Apply condition based on "watch('fourthPeriod')" and "diffOfDays"
                                  if (watch("fourthPeriod") < diffOfDays) {
                                    return (
                                      innerSum +
                                      (sale.salesReturnRemainAmount || 0)
                                    );
                                  }

                                  return innerSum;
                                }, 0)
                              );
                            }, 0)
                          )?.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow className="table-row">
                        <TableCell
                          component="td"
                          scope="row"
                          className="table-td text-center"
                          colSpan={"100%"}
                        >
                          No Data Found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TableContainer>
          </Index.Box>
        </Index.Box>
      );
    } else if (
      watch("outStandingReport") == "customermonthwise" ||
      watch("outStandingReport") == "suppliermonthwise"
    ) {
      return (
        <Index.Box className="card-spacing" style={{ display: "none" }}>
          <Index.Box className="page-table-main web-view-outstanding-report-table-main">
            <TableContainer
              component={Paper}
              className="table-container summary-table-container"
            >
              <div ref={printRef}>
                <Table
                  sx={{ minWidth: 650 }}
                  aria-label="simple table"
                  className="table"
                >
                  <TableHead className="table-head">
                    <TableRow>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-th"
                        colSpan="100%"
                      >
                        <Index.Box className="table-summary-bg">
                          <Index.Typography
                            variant="h4"
                            component="h4"
                            className="view-titles"
                          >
                            {watch("outStandingReport") == "customermonthwise"
                              ? "Customer"
                              : "Supplier"}{" "}
                            Outstanding Monthly Summary
                          </Index.Typography>
                          <Index.Typography
                            variant="h5"
                            component="h5"
                            className="view-subtitle"
                          ></Index.Typography>
                        </Index.Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody className="table-body">
                    <TableRow className="table-row">
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-th text-align-right"
                      >
                        Party Name
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-th text-align-right"
                      >
                        Sales Man
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-th text-align-right"
                      >
                        Opning
                      </TableCell>
                      {dateHeader?.map((month) => {
                        return (
                          <TableCell
                            component="th"
                            scope="row"
                            className="table-th text-align-right"
                          >
                            {month?.date}
                          </TableCell>
                        );
                      })}
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-th text-align-right"
                      >
                        Un-Adj
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-th text-align-right"
                      >
                        Total
                      </TableCell>
                    </TableRow>
                    {filterReportData?.map((row, index) => {
                      return (
                        <>
                          <TableRow className="table-row">
                            <TableCell
                              component="td"
                              scope="row"
                              className="table-td"
                            >
                              {row?.accountName}
                            </TableCell>
                            <TableCell
                              component="td"
                              scope="row"
                              className="table-td text-align-right"
                            >
                              {row?.brokerId}
                            </TableCell>
                            <TableCell
                              component="td"
                              scope="row"
                              className="table-td text-align-right"
                            >
                              0.00
                            </TableCell>
                            {dateHeader?.map((month) => {
                              // let isMatchMonth = moment(row?.salesData?.monthName, "MMM-YY").isSame(moment(month?.matchDate, "MMM-YY"), "month");
                              const rowData = row?.salesData?.find((ele) =>
                                moment(ele?.monthName, "MMM-YY").isSame(
                                  moment(month?.matchDate, "MMM-YY"),
                                  "month"
                                )
                              );
                              return (
                                <TableCell
                                  component="th"
                                  scope="row"
                                  className="table-th text-align-right"
                                >
                                  {rowData?.totalSalesReturnRemain || 0.0}
                                </TableCell>
                              );
                            })}
                            <TableCell
                              component="td"
                              scope="row"
                              className="table-td"
                            >
                              {/* {
                                                                Number(
                                                                    row?.salesData?.reduce((sum, sale) => {
                                                                        const accountTotal = parseData(sale?.accountData)?.reduce((acc, account) => {
                                                                            const amount = account?.amount || 0;
                                                                            return (acc || 0) + (isNaN(amount) ? 0 : amount);
                                                                        }, 0) || 0;

                                                                        return (sum || 0) + accountTotal;
                                                                    }, 0)?.toFixed(2)
                                                                )
                                                            } */}
                              {Number(
                                row?.advanceData
                                  ?.reduce((sum, sale) => {
                                    return (
                                      (sum || 0) +
                                      (sale?.totalAdvanceAmount || 0) || 0
                                    );
                                  }, 0)
                                  .toFixed(2) || 0
                              )}
                            </TableCell>
                            <TableCell
                              component="td"
                              scope="row"
                              className="table-td text-align-right"
                            >
                              {(
                                Number(
                                  row?.salesData?.reduce((acc, account) => {
                                    const amount =
                                      account?.totalSalesReturnRemain || 0;
                                    return (
                                      (acc || 0) + (isNaN(amount) ? 0 : amount)
                                    );
                                  }, 0) || 0
                                ) -
                                Number(
                                  row?.advanceData
                                    ?.reduce((sum, sale) => {
                                      return (
                                        (sum || 0) +
                                        (sale?.totalAdvanceAmount || 0) || 0
                                      );
                                    }, 0)
                                    .toFixed(2) || 0
                                )
                              )?.toFixed(2)}
                              {/* {
                                                                (Number(
                                                                    row?.salesData?.reduce((acc, account) => {
                                                                        const amount = account?.totalSalesReturnRemain || 0;
                                                                        return (acc || 0) + (isNaN(amount) ? 0 : amount);
                                                                    }, 0) || 0
                                                                ) -
                                                                    Number(
                                                                        row?.salesData?.reduce((sum, sale) => {
                                                                            const accountTotal = parseData(sale?.accountData)?.reduce((acc, account) => {
                                                                                const amount = account?.amount || 0;
                                                                                return (acc || 0) + (isNaN(amount) ? 0 : amount);
                                                                            }, 0) || 0;

                                                                            return (sum || 0) + accountTotal;
                                                                        }, 0)
                                                                    ))?.toFixed(2)
                                                            } */}
                            </TableCell>
                          </TableRow>
                        </>
                      );
                    })}

                    {filterReportData?.length > 0 ? (
                      <TableRow className="table-row">
                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th"
                        >
                          {" "}
                          Grand Total{" "}
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th"
                        >
                          {/* {Number(filterReportData?.reduce(
                                                            (reportDataSum, report) =>
                                                                reportDataSum +
                                                                (report?.salesData?.reduce(
                                                                    (salesSum, sale) =>
                                                                        salesSum +
                                                                        (sale?.totalAmount || 0),
                                                                    0
                                                                ) || 0),
                                                            0
                                                        ) || 0).
                                                            toFixed(2)} */}
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th"
                        >
                          {/* {Number(filterReportData?.reduce(
                                                            (reportDataSum, report) =>
                                                                reportDataSum +
                                                                (report?.salesData?.reduce(
                                                                    (salesSum, sale) =>
                                                                        salesSum +
                                                                        (sale?.accountData?.reduce(
                                                                            (subSum, entry) => subSum + (entry?.discount || 0),
                                                                            0
                                                                        ) || 0),
                                                                    0
                                                                ) || 0),
                                                            0
                                                        ) || 0).
                                                            toFixed(2)} */}
                        </TableCell>

                        {dateHeader?.map((month) => {
                          return (
                            <TableCell
                              component="th"
                              scope="row"
                              className="table-th text-align-right"
                            >
                              {Number(
                                filterReportData?.reduce(
                                  (reportDataSum, report) => {
                                    const matchingSalesSum =
                                      report?.salesData?.reduce(
                                        (salesSum, sale) => {
                                          if (
                                            moment(
                                              sale?.monthName,
                                              "MMM-YY"
                                            ).isSame(
                                              moment(
                                                month?.matchDate,
                                                "MMM-YY"
                                              ),
                                              "month"
                                            )
                                          ) {
                                            return (
                                              (salesSum || 0) +
                                              (sale?.totalSalesReturnRemain ||
                                                0)
                                            );
                                          }
                                          return salesSum || 0;
                                        },
                                        0
                                      ) || 0;

                                    return reportDataSum + matchingSalesSum;
                                  },
                                  0
                                ) || 0
                              ).toFixed(2)}
                            </TableCell>
                          );
                        })}

                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th"
                        >
                          {/* {Number(
                                                            filterReportData?.reduce(
                                                                (reportDataSum, report) =>
                                                                    (reportDataSum || 0) +
                                                                    (report?.salesData?.reduce(
                                                                        (salesSum, sale) =>
                                                                            (salesSum || 0) +
                                                                            (parseData(sale?.accountData || [])?.reduce(
                                                                                (subSum, entry) => (subSum || 0) + (entry?.amount || 0),
                                                                                0
                                                                            ) || 0),
                                                                        0
                                                                    ) || 0),
                                                                0
                                                            ) || 0
                                                        ).toFixed(2)} */}

                          {Number(
                            filterReportData
                              ?.reduce(
                                (reportDataSum, report) =>
                                  reportDataSum +
                                  (report?.advanceData?.reduce(
                                    (salesSum, sale) =>
                                      salesSum +
                                      (sale?.totalAdvanceAmount || 0),
                                    0
                                  ) || 0),
                                0
                              )
                              .toFixed(2)
                          ) || 0}
                        </TableCell>

                        <TableCell
                          component="th"
                          scope="row"
                          className="table-th"
                        >
                          {/* {
                                                            (Number(
                                                                filterReportData?.reduce((sum, account) => {
                                                                    return (sum || 0) + account?.salesData?.reduce((innerSum, sale) => {
                                                                        return (innerSum || 0) + (sale.totalSalesReturnRemain || 0);
                                                                    }, 0);
                                                                }, 0)
                                                            ) -
                                                                Number(
                                                                    filterReportData?.reduce(
                                                                        (reportDataSum, report) =>
                                                                            (reportDataSum || 0) +
                                                                            (report?.salesData?.reduce(
                                                                                (salesSum, sale) =>
                                                                                    (salesSum || 0) +
                                                                                    (parseData(sale?.accountData || [])?.reduce(
                                                                                        (subSum, entry) => (subSum || 0) + (entry?.amount || 0),
                                                                                        0
                                                                                    ) || 0),
                                                                                0
                                                                            ) || 0),
                                                                        0
                                                                    ) || 0
                                                                )).toFixed(2)} */}

                          {(
                            Number(
                              filterReportData?.reduce((sum, account) => {
                                return (
                                  (sum || 0) +
                                  account?.salesData?.reduce(
                                    (innerSum, sale) => {
                                      return (
                                        (innerSum || 0) +
                                        (sale.totalSalesReturnRemain || 0)
                                      );
                                    },
                                    0
                                  )
                                );
                              }, 0)
                            ) -
                            Number(
                              filterReportData
                                ?.reduce(
                                  (reportDataSum, report) =>
                                    reportDataSum +
                                    (report?.advanceData?.reduce(
                                      (salesSum, sale) =>
                                        salesSum +
                                        (sale?.totalAdvanceAmount || 0),
                                      0
                                    ) || 0),
                                  0
                                )
                                .toFixed(2)
                            ) || 0
                          ).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow className="table-row">
                        <TableCell
                          component="td"
                          scope="row"
                          className="table-td text-center"
                          colSpan={"100%"}
                        >
                          No Data Found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TableContainer>
          </Index.Box>
        </Index.Box>
      );
    }
  };

  if (loading) {
    <LoadingSpinner></LoadingSpinner>;
  } else {
    return (
      <>
        <Index.Box className="main_content">
          <Index.Box className="web-main-content">
            <Index.Box className="over-text-scroll">
              <Index.Box className="admin-dashboard-list-row">
                <Index.Box
                  className="report-main-box report-main-box-set"
                  ref={filterRef}
                >
                  <form
                    className="form-content"
                    onSubmit={handleSubmit(submit)}
                  >
                    <Index.Box className="input-box stock-report-input-box webview-input-box">
                      <Index.Box className="form-group">
                        <Index.FormHelperText className="lable-control">
                          OutStanding Report{" "}
                          <span className="requried-star">*</span>
                        </Index.FormHelperText>
                        <Index.Box className="dropdown-box auto-drop-box">
                          <Controller
                            control={control}
                            {...register("outStandingReport", {
                              required: "OutStanding Report is required.",
                            })}
                            error={Boolean(errors.outStandingReport)}
                            render={({ field: { onChange, value } }) => (
                              <Autocomplete
                                className="custom-form-control"
                                options={outStandingData || []}
                                autoHighlight
                                onChange={(e, value) => {
                                  onChange(value?.id ? value?.id : "");
                                  setValue("selectCustomerWise", []);
                                  setValue("selectSupplierWise", []);
                                  setValue("isSubmitted", 0);
                                  setValue("select_group", []);
                                  setValue("selectCityWise", []);
                                  setValue("selectStateWise", []);
                                  setValue("selectAreaWise", []);
                                  setFilterReportData([]);
                                  setBrokerReportData([]);
                                  setReportData([]);

                                  setValue("IgnoreCrDays", false);
                                  setValue("printWithPaymentDetails", false);
                                  setValue("poNumber", false);
                                  setValue("category", false);
                                  setValue("monthWiseTotal", false);

                                  setValue("ignorePayment", false);
                                  setValue("printTel", false);
                                  setValue("printOnLetter", false);
                                  setValue("AgeWiseAnalysis", false);
                                  setValue("ageSummary", false);
                                  setValue("subBroker", false);
                                  setValue("collectionLetter", false);
                                }}
                                getOptionLabel={(option) => option?.partyName}
                                renderOption={(props, option) => (
                                  <Index.Box
                                    component="li"
                                    sx={{
                                      "& > img": {
                                        mr: 2,
                                        flexShrink: 0,
                                      },
                                    }}
                                    {...props}
                                  >
                                    {option?.partyName}
                                  </Index.Box>
                                )}
                                renderInput={(params) => (
                                  <Index.TextField
                                    fullWidth
                                    error={Boolean(errors?.outStandingReport)}
                                    className="inner-custom-form-control"
                                    {...params}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              />
                            )}
                          />
                        </Index.Box>
                      </Index.Box>
                    </Index.Box>

                    <Index.Box className="input-box stock-report-input-box webview-input-box">
                      <Index.Box className="form-group">
                        <Index.FormHelperText className="lable-control">
                          Show <span className="requried-star">*</span>
                        </Index.FormHelperText>
                        <Index.Box className="dropdown-box auto-drop-box">
                          <Controller
                            control={control}
                            {...register("show", {
                              required: "Show is required.",
                            })}
                            error={Boolean(errors.show)}
                            render={({ field: { onChange, value } }) => (
                              <Autocomplete
                                className="custom-form-control"
                                options={stockTypeList || []}
                                value={
                                  stockTypeList.find(
                                    (option) => option.id === value
                                  ) || null
                                }
                                autoHighlight
                                onChange={(e, value) => {
                                  onChange(value?.id ? value?.id : "");
                                  setFilterReportData([]);
                                  setReportData([]);
                                  setBrokerReportData([]);
                                }}
                                getOptionLabel={(option) => option?.show}
                                renderOption={(props, option) => (
                                  <Index.Box
                                    component="li"
                                    sx={{
                                      "& > img": {
                                        mr: 2,
                                        flexShrink: 0,
                                      },
                                    }}
                                    {...props}
                                  >
                                    {option?.show}
                                  </Index.Box>
                                )}
                                renderInput={(params) => (
                                  <Index.TextField
                                    fullWidth
                                    error={Boolean(errors?.show)}
                                    className="inner-custom-form-control"
                                    {...params}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              />
                            )}
                          />
                        </Index.Box>
                      </Index.Box>
                    </Index.Box>

                    {watch("show") == "date" && (
                      <>
                        <Index.Box className="input-box stock-report-input-box webview-input-box">
                          <Index.Box className="form-group date-of-purchase">
                            <Index.FormHelperText className="lable-control">
                              Start Date
                            </Index.FormHelperText>

                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DemoContainer components={["DatePicker"]}>
                                <Controller
                                  control={control}
                                  {...register("startDate", {
                                    required:
                                      watch("show") == "date"
                                        ? "start date is required."
                                        : false,
                                  })}
                                  error={
                                    watch("show") == "date"
                                      ? Boolean(errors.startDate)
                                      : false
                                  }
                                  render={({ field: { onChange, value } }) => (
                                    <MobileDatePicker
                                      // disableFuture={true}
                                      className="custom-form-control"
                                      format="DD/MM/YYYY"
                                      value={value}
                                      minDate={dayjs(selectedYearStart)}
                                      maxDate={dayjs(selectedYearEnd)}
                                      onChange={(newValue) => {
                                        setValue("startDate", newValue);
                                      }}
                                      error={
                                        watch("show") == "date"
                                          ? Boolean(errors.startDate)
                                          : false
                                      }
                                    />
                                  )}
                                />
                              </DemoContainer>
                            </LocalizationProvider>
                          </Index.Box>
                        </Index.Box>

                        <Index.Box className="input-box stock-report-input-box webview-input-box">
                          <Index.Box className="form-group date-of-purchase">
                            <Index.FormHelperText className="lable-control">
                              End Date
                            </Index.FormHelperText>

                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DemoContainer components={["DatePicker"]}>
                                <Controller
                                  control={control}
                                  {...register("endDate", {
                                    required:
                                      watch("show") == "date" &&
                                        watch("startDate")
                                        ? "end date is required."
                                        : false,
                                  })}
                                  error={
                                    watch("show") == "date" &&
                                      watch("startDate")
                                      ? Boolean(errors.endDate)
                                      : false
                                  }
                                  render={({ field: { onChange, value } }) => (
                                    <MobileDatePicker
                                      className="custom-form-control"
                                      format="DD/MM/YYYY"
                                      value={value}
                                      minDate={
                                        watch("startDate")
                                          ? dayjs(watch("startDate"))
                                          : null
                                      }
                                      maxDate={
                                        watch("startDate")
                                          ? dayjs(watch("startDate")).add(
                                            1,
                                            "year"
                                          )
                                          : null
                                      }
                                      onChange={(newValue) => {
                                        setValue("endDate", newValue); // Set end date
                                      }}
                                      error={
                                        watch("show") == "date" &&
                                          watch("startDate")
                                          ? Boolean(errors.endDate)
                                          : false
                                      }
                                    />
                                  )}
                                />
                              </DemoContainer>
                            </LocalizationProvider>
                          </Index.Box>
                        </Index.Box>
                      </>
                    )}

                    <Index.Box className="input-box stock-report-input-box webview-input-box">
                      <Index.Box className="form-group">
                        <Index.FormHelperText className="lable-control">
                          Location
                        </Index.FormHelperText>
                        <Index.Box className="dropdown-box auto-drop-box">
                          <Controller
                            control={control}
                            {...register("location")}
                            render={({ field: { onChange, value } }) => (
                              <Autocomplete
                                className="custom-form-control"
                                options={partyList || []}
                                value={
                                  partyList.find(
                                    (option) => option?.id === value
                                  ) || null
                                }
                                autoHighlight
                                onChange={(e, value) => {
                                  onChange(value?.id ? value?.id : "");
                                  // setValue("preference", "")
                                  setValue("selectCityWise", []);
                                  setValue("selectStateWise", []);
                                  setValue("selectAreaWise", []);
                                }}
                                getOptionLabel={(option) => option?.partyName}
                                renderOption={(props, option) => (
                                  <Index.Box
                                    component="li"
                                    sx={{
                                      "& > img": {
                                        mr: 2,
                                        flexShrink: 0,
                                      },
                                    }}
                                    {...props}
                                  >
                                    {option?.partyName}
                                  </Index.Box>
                                )}
                                renderInput={(params) => (
                                  <Index.TextField
                                    fullWidth
                                    error={Boolean(errors?.group)}
                                    className="inner-custom-form-control"
                                    {...params}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              />
                            )}
                          />
                        </Index.Box>
                      </Index.Box>
                    </Index.Box>

                    {watch("location") && (
                      <Index.Box className="input-box stock-report-input-box webview-input-box">
                        <Index.Box className="form-group">
                          <Index.FormHelperText className="lable-control">
                            Preference
                          </Index.FormHelperText>
                          <Index.Box className="dropdown-box auto-drop-box">
                            <Controller
                              control={control}
                              {...register("preference")}
                              render={({ field: { onChange, value } }) => {
                                return (
                                  <Autocomplete
                                    className="custom-form-control"
                                    options={selectData || []}
                                    autoHighlight
                                    value={
                                      selectData.find(
                                        (option) => option?.id === value
                                      ) || null
                                    }
                                    onChange={(e, value) => {
                                      onChange(value?.id ? value?.id : "");
                                      setValue("selectCityWise", []);
                                      setValue("selectStateWise", []);
                                      setValue("selectAreaWise", []);
                                    }}
                                    getOptionLabel={(option) => option?.type}
                                    renderOption={(props, option) => (
                                      <Index.Box
                                        component="li"
                                        sx={{
                                          "& > img": {
                                            mr: 2,
                                            flexShrink: 0,
                                          },
                                        }}
                                        {...props}
                                      >
                                        {option?.type}
                                      </Index.Box>
                                    )}
                                    renderInput={(params) => (
                                      <Index.TextField
                                        fullWidth
                                        error={Boolean(errors?.productCategory)}
                                        className="inner-custom-form-control"
                                        {...params}
                                        size="small"
                                        variant="outlined"
                                      />
                                    )}
                                  />
                                );
                              }}
                            />
                          </Index.Box>
                        </Index.Box>
                      </Index.Box>
                    )}

                    {watch("preference") == "selected" &&
                      watch("location") == "citywise" && (
                        <Index.Box className="input-box stock-report-input-box webview-input-box">
                          <Index.Box className="form-group">
                            <Index.FormHelperText className="lable-control">
                              Select City
                            </Index.FormHelperText>
                            <Index.Box className="dropdown-box auto-drop-box">
                              <Controller
                                control={control}
                                {...register("selectCityWise", {
                                  required:
                                    watch("preference") == "selected" &&
                                      watch("location") == "citywise"
                                      ? "preference is required."
                                      : false,
                                })}
                                error={
                                  watch("preference") == "selected" &&
                                    watch("location") == "citywise"
                                    ? Boolean(errors.selectCityWise)
                                    : false
                                }
                                render={({ field: { onChange, value } }) => (
                                  <Autocomplete
                                    className="custom-form-control"
                                    multiple
                                    options={cityWise || []}
                                    autoHighlight
                                    value={
                                      cityWise?.filter((option) =>
                                        value?.includes(option?.ID)
                                      ) || []
                                    }
                                    onChange={(e, value) => {
                                      onChange(value?.map((option) => option?.ID));
                                    }}
                                    getOptionLabel={(option) => option?.city}
                                    renderOption={(props, option) => (
                                      <Index.Box
                                        component="li"
                                        sx={{
                                          "& > img": {
                                            mr: 2,
                                            flexShrink: 0,
                                          },
                                        }}
                                        {...props}
                                      >
                                        {option?.city}
                                      </Index.Box>
                                    )}
                                    renderInput={(params) => (
                                      <Index.TextField
                                        fullWidth
                                        error={
                                          watch("preference") == "selected" &&
                                            watch("location") == "citywise"
                                            ? Boolean(errors?.selectCityWise)
                                            : false
                                        }
                                        className="inner-custom-form-control"
                                        {...params}
                                        size="small"
                                        variant="outlined"
                                      />
                                    )}
                                  />
                                )}
                              />
                            </Index.Box>
                          </Index.Box>
                        </Index.Box>
                      )}
                    {watch("preference") == "selected" &&
                      watch("location") == "areawise" && (
                        <Index.Box className="input-box stock-report-input-box webview-input-box">
                          <Index.Box className="form-group">
                            <Index.FormHelperText className="lable-control">
                              Select Area
                            </Index.FormHelperText>
                            <Index.Box className="dropdown-box auto-drop-box">
                              <Controller
                                control={control}
                                {...register("selectAreaWise", {
                                  required:
                                    watch("preference") == "selected" &&
                                      watch("location") == "areawise"
                                      ? "Customer is required."
                                      : false,
                                })}
                                error={
                                  watch("preference") == "selected" &&
                                    watch("location") == "areawise"
                                    ? Boolean(errors.selectAreaWise)
                                    : false
                                }
                                render={({ field: { onChange, value } }) => (
                                  <Autocomplete
                                    className="custom-form-control"
                                    multiple
                                    options={areawise || []}
                                    autoHighlight
                                    value={
                                      areawise?.filter((option) =>
                                        value?.includes(option?.area_code)
                                      ) || []
                                    }
                                    onChange={(e, value) => {
                                      onChange(
                                        value?.map((option) => option?.area_code)
                                      );
                                    }}
                                    getOptionLabel={(option) =>
                                      option?.area_name
                                    }
                                    renderOption={(props, option) => (
                                      <Index.Box
                                        component="li"
                                        sx={{
                                          "& > img": {
                                            mr: 2,
                                            flexShrink: 0,
                                          },
                                        }}
                                        {...props}
                                      >
                                        {option?.area_name}
                                      </Index.Box>
                                    )}
                                    renderInput={(params) => (
                                      <Index.TextField
                                        fullWidth
                                        error={
                                          watch("preference") == "selected" &&
                                            watch("location") == "areawise"
                                            ? Boolean(errors?.selectAreaWise)
                                            : false
                                        }
                                        className="inner-custom-form-control"
                                        {...params}
                                        size="small"
                                        variant="outlined"
                                      />
                                    )}
                                  />
                                )}
                              />
                            </Index.Box>
                          </Index.Box>
                        </Index.Box>
                      )}
                    {watch("preference") == "selected" &&
                      watch("location") == "statewise" && (
                        <Index.Box className="input-box stock-report-input-box webview-input-box">
                          <Index.Box className="form-group">
                            <Index.FormHelperText className="lable-control">
                              Select State
                            </Index.FormHelperText>
                            <Index.Box className="dropdown-box auto-drop-box">
                              <Controller
                                control={control}
                                {...register("selectStateWise", {
                                  required:
                                    watch("preference") == "selected" &&
                                      watch("location") == "statewise"
                                      ? "preference is required."
                                      : false,
                                })}
                                error={
                                  watch("preference") == "selected" &&
                                    watch("location") == "statewise"
                                    ? Boolean(errors.selectStateWise)
                                    : false
                                }
                                render={({ field: { onChange, value } }) => (
                                  <Autocomplete
                                    className="custom-form-control"
                                    multiple
                                    options={stateWise || []}
                                    value={
                                      stateWise?.filter((option) =>
                                        value?.includes(option?.Statecode)
                                      ) || []
                                    }
                                    autoHighlight
                                    onChange={(e, value) => {
                                      onChange(
                                        value?.map((option) => option?.Statecode)
                                      );
                                    }}
                                    getOptionLabel={(option) =>
                                      option?.Statename
                                    }
                                    renderOption={(props, option) => (
                                      <Index.Box
                                        component="li"
                                        sx={{
                                          "& > img": {
                                            mr: 2,
                                            flexShrink: 0,
                                          },
                                        }}
                                        {...props}
                                      >
                                        {option?.Statename}
                                      </Index.Box>
                                    )}
                                    renderInput={(params) => (
                                      <Index.TextField
                                        fullWidth
                                        error={
                                          watch("preference") == "selected" &&
                                            watch("location") == "statewise"
                                            ? Boolean(errors?.selectStateWise)
                                            : false
                                        }
                                        className="inner-custom-form-control"
                                        {...params}
                                        size="small"
                                        variant="outlined"
                                      />
                                    )}
                                  />
                                )}
                              />
                            </Index.Box>
                          </Index.Box>
                        </Index.Box>
                      )}
                    {(watch("outStandingReport") == "customerbroker" ||
                      watch("outStandingReport") == "supplierbroker") && (
                        <Index.Box
                          gridColumn={{
                            xs: "span 12",
                            sm: "span 6",
                            md: "span 4",
                            lg: "span 4",
                          }}
                          className="grid-column"
                        >
                          <Index.Box
                            gridColumn={{
                              xs: "span 12",
                              sm: "span 12",
                              md: "span 12",
                              lg: "span 12",
                            }}
                            className="grid-column"
                          >
                            <Item className="grid-item">
                              <Index.Box className="input-box stock-report-input-box webview-input-box">
                                <Index.Box className="form-group">
                                  <Index.FormHelperText className="lable-control">
                                    Select Broker
                                    {/* <span className="requried-star">*</span> */}
                                  </Index.FormHelperText>
                                  <RadioGroup
                                    row
                                    aria-labelledby="demo-row-radio-buttons-group-label"
                                    name="row-radio-buttons-group"
                                    className="radio-group"
                                    value={watch("brokerType")}
                                    onChange={(e, value) => {
                                      setValue("broker", "");
                                      setValue("selectCustomerWise", "");
                                      setValue("selectSupplierWise", "");
                                      setValue("brokerType", value);
                                    }}
                                  >
                                    <FormControlLabel
                                      value="all"
                                      checked={watch("brokerType") == "all"}
                                      control={<Radio />}
                                      label="All"
                                      className="radio-control"
                                    />
                                    <FormControlLabel
                                      value="selected"
                                      checked={watch("brokerType") == "selected"}
                                      control={<Radio />}
                                      label="selected"
                                      className="radio-control"
                                    />
                                  </RadioGroup>
                                </Index.Box>
                              </Index.Box>
                            </Item>
                          </Index.Box>
                        </Index.Box>
                      )}

                    {(watch("outStandingReport") == "customerbroker" ||
                      watch("outStandingReport") == "supplierbroker") &&
                      watch("brokerType") == "selected" && (
                        <Index.Box
                          gridColumn={{
                            xs: "span 12",
                            sm: "span 6",
                            md: "span 4",
                            lg: "span 4",
                          }}
                          className="grid-column"
                        >
                          <Index.Box className="input-box stock-report-input-box webview-input-box">
                            <Index.Box className="form-group">
                              <Index.FormHelperText className="lable-control">
                                Broker <span className="requried-star">*</span>
                              </Index.FormHelperText>
                              <Index.Box className="dropdown-box auto-drop-box">
                                <Controller
                                  control={control}
                                  {...register("broker", {
                                    required:
                                      (watch("outStandingReport") ==
                                        "customerbroker" ||
                                        watch("outStandingReport") ==
                                        "supplierbroker") &&
                                        watch("brokerType") == "selected"
                                        ? "broker is required."
                                        : false,
                                  })}
                                  error={
                                    (watch("outStandingReport") ==
                                      "customerbroker" ||
                                      watch("outStandingReport") ==
                                      "supplierbroker") &&
                                      watch("brokerType") == "selected"
                                      ? Boolean(errors.broker)
                                      : false
                                  }
                                  render={({ field: { onChange, value } }) => (
                                    <Autocomplete
                                      className="custom-form-control"
                                      options={broker || []}
                                      value={
                                        broker.find(
                                          (option) => option?.groupId === value
                                        ) || null
                                      }
                                      autoHighlight
                                      onChange={(e, value) => {
                                        onChange(
                                          value?.groupId ? value?.groupId : ""
                                        );
                                      }}
                                      getOptionLabel={(option) =>
                                        option?.accountName
                                      }
                                      renderOption={(props, option) => (
                                        <Index.Box
                                          component="li"
                                          sx={{
                                            "& > img": {
                                              mr: 2,
                                              flexShrink: 0,
                                            },
                                          }}
                                          {...props}
                                        >
                                          {option?.accountName}
                                        </Index.Box>
                                      )}
                                      renderInput={(params) => (
                                        <Index.TextField
                                          fullWidth
                                          error={
                                            (watch("outStandingReport") ==
                                              "customerbroker" ||
                                              watch("outStandingReport") ==
                                              "supplierbroker") &&
                                              watch("brokerType") == "selected"
                                              ? Boolean(errors?.broker)
                                              : false
                                          }
                                          className="inner-custom-form-control"
                                          {...params}
                                          size="small"
                                          variant="outlined"
                                        />
                                      )}
                                    />
                                  )}
                                />
                              </Index.Box>
                            </Index.Box>
                          </Index.Box>
                        </Index.Box>
                      )}

                    <Index.Box
                      gridColumn={{
                        xs: "span 12",
                        sm: "span 6",
                        md: "span 4",
                        lg: "span 4",
                      }}
                      className="grid-column"
                    >
                      <Index.Box className="input-box stock-report-input-box webview-input-box">
                        <Index.Box className="form-group">
                          <Index.FormHelperText className="lable-control">
                            Group Wise
                          </Index.FormHelperText>
                          <Index.Box className="dropdown-box auto-drop-box">
                            <Controller
                              control={control}
                              {...register("group_wise")}
                              render={({ field: { onChange, value } }) => (
                                <Autocomplete
                                  className="custom-form-control"
                                  options={selectData || []}
                                  value={
                                    selectData.find(
                                      (option) => option?.id === value
                                    ) || null
                                  }
                                  autoHighlight
                                  onChange={(e, value) => {
                                    onChange(value?.id ? value?.id : "");
                                    setValue("select_group", []);
                                  }}
                                  getOptionLabel={(option) => option?.type}
                                  renderOption={(props, option) => (
                                    <Index.Box
                                      component="li"
                                      sx={{
                                        "& > img": {
                                          mr: 2,
                                          flexShrink: 0,
                                        },
                                      }}
                                      {...props}
                                    >
                                      {option?.type}
                                    </Index.Box>
                                  )}
                                  renderInput={(params) => (
                                    <Index.TextField
                                      fullWidth
                                      error={Boolean(errors?.productCategory)}
                                      className="inner-custom-form-control"
                                      {...params}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                />
                              )}
                            />
                          </Index.Box>
                        </Index.Box>
                      </Index.Box>
                    </Index.Box>

                    {watch("group_wise") == "selected" && (
                      <Index.Box
                        gridColumn={{
                          xs: "span 12",
                          sm: "span 6",
                          md: "span 4",
                          lg: "span 4",
                        }}
                        className="grid-column"
                      >
                        <Index.Box className="input-box stock-report-input-box webview-input-box">
                          <Index.Box className="form-group">
                            <Index.FormHelperText className="lable-control">
                              Select Group
                            </Index.FormHelperText>
                            <Index.Box className="dropdown-box auto-drop-box">
                              <Controller
                                control={control}
                                {...register("select_group", {
                                  required:
                                    watch("group_wise") == "selected"
                                      ? "Customer is required."
                                      : false,
                                })}
                                error={
                                  watch("group_wise") == "selected"
                                    ? Boolean(errors.select_group)
                                    : false
                                }
                                render={({ field: { onChange, value } }) => (
                                  <Autocomplete
                                    className="custom-form-control"
                                    multiple
                                    options={
                                      watch("outStandingReport")?.includes(
                                        "broker"
                                      )
                                        ? groupWise?.filter(
                                          (ele) => ele?.Id == watch("broker")
                                        ) || []
                                        : watch("outStandingReport")?.includes(
                                          "customer"
                                        )
                                          ? groupWise?.filter((item) =>
                                            item?.GroupName?.includes(
                                              "SUNDRY DEBTORS"
                                            )
                                          ) || []
                                          : groupWise?.filter((item) =>
                                            item?.GroupName?.includes(
                                              "SUNDRY CREDITORS"
                                            )
                                          ) || []
                                    }
                                    autoHighlight
                                    value={
                                      groupWise?.filter((option) =>
                                        value?.includes(option?.Id)
                                      ) || []
                                    }
                                    onChange={(e, value) => {
                                      onChange(value?.map((option) => option?.Id));
                                    }}
                                    getOptionLabel={(option) =>
                                      option?.GroupName
                                    }
                                    renderOption={(props, option) => (
                                      <Index.Box
                                        component="li"
                                        sx={{
                                          "& > img": {
                                            mr: 2,
                                            flexShrink: 0,
                                          },
                                        }}
                                        {...props}
                                      >
                                        {option?.GroupName}
                                      </Index.Box>
                                    )}
                                    renderInput={(params) => (
                                      <Index.TextField
                                        fullWidth
                                        error={
                                          watch("group_wise") == "selected"
                                            ? Boolean(errors?.select_group)
                                            : false
                                        }
                                        className="inner-custom-form-control"
                                        {...params}
                                        size="small"
                                        variant="outlined"
                                      />
                                    )}
                                  />
                                )}
                              />
                            </Index.Box>
                          </Index.Box>
                        </Index.Box>
                      </Index.Box>
                    )}

                    <Index.Box className="input-box stock-report-input-box webview-input-box">
                      <Index.Box className="form-group date-of-purchase">
                        <Index.FormHelperText className="lable-control">
                          Till
                        </Index.FormHelperText>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DemoContainer components={["DatePicker"]}>
                            <Controller
                              control={control}
                              {...register("till")}
                              render={({ field: { onChange, value } }) => (
                                <MobileDatePicker
                                  // disableFuture={true}
                                  className="custom-form-control"
                                  format="DD/MM/YYYY"
                                  value={value}
                                  onChange={(newValue) => {
                                    setValue("till", newValue);
                                  }}
                                  error={Boolean(errors.fromDate)}
                                />
                              )}
                            />
                          </DemoContainer>
                        </LocalizationProvider>
                      </Index.Box>
                    </Index.Box>

                    <Index.Box className="input-box stock-report-input-box webview-input-box">
                      <Index.Box className="form-group date-of-purchase">
                        <Index.FormHelperText className="lable-control">
                          Letter Date
                        </Index.FormHelperText>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DemoContainer components={["DatePicker"]}>
                            <Controller
                              control={control}
                              {...register("latterDate")}
                              render={({ field: { onChange, value } }) => (
                                <MobileDatePicker
                                  // disableFuture={true}
                                  className="custom-form-control"
                                  format="DD/MM/YYYY"
                                  value={value}
                                  onChange={(newValue) => {
                                    setValue("latterDate", newValue);
                                  }}
                                // error={Boolean(errors.latterDate)}
                                />
                              )}
                            />
                          </DemoContainer>
                        </LocalizationProvider>
                      </Index.Box>
                    </Index.Box>

                    <Index.Box className="input-box stock-report-input-box webview-input-box">
                      <Index.Box className="form-group">
                        <Index.FormHelperText className="lable-control">
                          {watch("outStandingReport")?.includes("supplier")
                            ? "Supplier"
                            : "Customer"}
                          {/* <span className="requried-star">*</span> */}
                        </Index.FormHelperText>
                        <RadioGroup
                          row
                          aria-labelledby="demo-row-radio-buttons-group-label"
                          name="row-radio-buttons-group"
                          className="radio-group"
                          value={watch("customer")}
                          onChange={(e, value) => {
                            setValue("broker", "");
                            setValue("selectCustomerWise", []);
                            setValue("selectSupplierWise", []);
                            setValue("customer", value);
                          }}
                        >
                          <FormControlLabel
                            value="all"
                            checked={watch("customer") == "all"}
                            control={<Radio />}
                            label="All"
                            className="radio-control"
                          />
                          <FormControlLabel
                            value="selected"
                            checked={watch("customer") == "selected"}
                            control={<Radio />}
                            label="selected"
                            className="radio-control"
                          />
                        </RadioGroup>
                      </Index.Box>
                    </Index.Box>

                    {watch("customer") == "selected" &&
                      watch("outStandingReport")?.includes("customer") && (
                        <Index.Box className="input-box stock-report-input-box webview-input-box">
                          <Index.Box className="form-group">
                            <Index.FormHelperText className="lable-control">
                              Customer
                            </Index.FormHelperText>
                            <Index.Box className="dropdown-box auto-drop-box">
                              <Controller
                                control={control}
                                {...register("selectCustomerWise", {
                                  required:
                                    watch("customer") == "selected" &&
                                      watch("outStandingReport")?.includes(
                                        "customer"
                                      )
                                      ? "Customer is required."
                                      : false,
                                })}
                                error={
                                  watch("customer") == "selected" &&
                                    watch("outStandingReport")?.includes(
                                      "customer"
                                    )
                                    ? Boolean(errors.selectCustomerWise)
                                    : false
                                }
                                render={({ field: { onChange, value } }) => {
                                  return (
                                    <Autocomplete
                                      className="custom-form-control"
                                      multiple
                                      options={filterWiseCustomerParty || []}
                                      value={
                                        filterWiseCustomerParty?.filter(
                                          (option) =>
                                            value?.includes(option?.id)
                                        ) || []
                                      }
                                      autoHighlight
                                      onChange={(e, newValue) => {
                                        onChange(
                                          newValue?.map((option) => option?.id)
                                        );
                                      }}
                                      getOptionLabel={(option) =>
                                        option?.accountName
                                      }
                                      renderOption={(
                                        props,
                                        option,
                                        { selected }
                                      ) => (
                                        <Index.Box
                                          component="li"
                                          sx={{
                                            "& > img": {
                                              mr: 2,
                                              flexShrink: 0,
                                            },
                                            backgroundColor: selected
                                              ? "rgba(0, 0, 0, 0.08)"
                                              : "transparent",
                                          }}
                                          {...props}
                                        >
                                          {option?.accountName}
                                        </Index.Box>
                                      )}
                                      renderInput={(params) => (
                                        <Index.TextField
                                          fullWidth
                                          error={
                                            watch("customer") == "selected" &&
                                              watch(
                                                "outStandingReport"
                                              )?.includes("customer")
                                              ? Boolean(
                                                errors?.selectCustomerWise
                                              )
                                              : false
                                          }
                                          className="inner-custom-form-control"
                                          {...params}
                                          size="small"
                                          variant="outlined"
                                        />
                                      )}
                                    />
                                  );
                                }}
                              />
                            </Index.Box>
                          </Index.Box>
                        </Index.Box>
                      )}

                    {watch("customer") == "selected" &&
                      watch("outStandingReport")?.includes("supplier") && (
                        <Index.Box className="input-box stock-report-input-box webview-input-box">
                          <Index.Box className="form-group">
                            <Index.FormHelperText className="lable-control">
                              Supplier
                            </Index.FormHelperText>
                            <Index.Box className="dropdown-box auto-drop-box">
                              <Controller
                                control={control}
                                {...register("selectSupplierWise", {
                                  required:
                                    watch("customer") == "selected" &&
                                      watch("outStandingReport")?.includes(
                                        "supplier"
                                      )
                                      ? "Customer is required."
                                      : false,
                                })}
                                error={
                                  watch("customer") == "selected" &&
                                    watch("outStandingReport")?.includes(
                                      "supplier"
                                    )
                                    ? Boolean(errors.selectSupplierWise)
                                    : false
                                }
                                render={({ field: { onChange, value } }) => (
                                  <Autocomplete
                                    className="custom-form-control"
                                    options={filterWiseSupplierParty || []}
                                    // value={filterWiseSupplierParty.find((option) => option?.id === value) || null}
                                    value={
                                      filterWiseSupplierParty?.filter(
                                        (option) => value?.includes(option?.id)
                                      ) || []
                                    }
                                    autoHighlight
                                    multiple
                                    // onChange={(e, value) => {
                                    //     onChange(
                                    //         value?.id
                                    //             ? value?.id
                                    //             : ""
                                    //     );
                                    // }}
                                    onChange={(e, newValue) => {
                                      onChange(
                                        newValue.map((option) => option?.id)
                                      );
                                    }}
                                    getOptionLabel={(option) =>
                                      option?.accountName
                                    }
                                    renderOption={(props, option) => (
                                      <Index.Box
                                        component="li"
                                        sx={{
                                          "& > img": {
                                            mr: 2,
                                            flexShrink: 0,
                                          },
                                        }}
                                        {...props}
                                      >
                                        {option?.accountName}
                                      </Index.Box>
                                    )}
                                    renderInput={(params) => (
                                      <Index.TextField
                                        fullWidth
                                        error={
                                          watch("customer") == "selected" &&
                                            watch("outStandingReport")?.includes(
                                              "supplier"
                                            )
                                            ? Boolean(
                                              errors?.selectSupplierWise
                                            )
                                            : false
                                        }
                                        className="inner-custom-form-control"
                                        {...params}
                                        size="small"
                                        variant="outlined"
                                      />
                                    )}
                                  />
                                )}
                              />
                            </Index.Box>
                          </Index.Box>
                        </Index.Box>
                      )}

                    <Index.Box className="input-box stock-report-input-box webview-input-box">
                      <Index.Box className="form-group">
                        <Index.FormHelperText className="lable-control">
                          Letter
                        </Index.FormHelperText>
                        <Index.Box className="dropdown-box auto-drop-box">
                          <Index.TextField
                            fullWidth
                            type="text"
                            id="fullWidth"
                            className="custom-form-control"
                            // value={watch("brokerage")}
                            {...register("letter", {
                              // required: "letter is required.",
                            })}
                            onChange={(e) => {
                              setValue("letter", e.target.value);
                            }}
                          // error={Boolean(errors.letter)}
                          />
                        </Index.Box>
                      </Index.Box>
                    </Index.Box>

                    <Index.Box
                      gridColumn={{
                        xs: "span 12",
                        sm: "span 6",
                        md: "span 4",
                        lg: "span 4",
                      }}
                      className="grid-column"
                    >
                      <Index.Box sx={{ flexGrow: 1 }}>
                        <Index.Box className="input-box stock-report-input-box webview-input-box">
                          <Index.Box className="form-group">
                            <Index.FormHelperText className="lable-control"></Index.FormHelperText>
                            <Index.Box className="dropdown-box auto-drop-box">
                              <Index.Box className="checkbox-content-flex">
                                <Index.Box className="checkbox-main">
                                  <Controller
                                    className="checkbox"
                                    control={control}
                                    {...register("between")}
                                    render={({
                                      field: { onChange, value },
                                    }) => (
                                      <input
                                        type="checkbox"
                                        className="custom-form-control checkbox"
                                        checked={
                                          watch("between") ? true : false
                                        }
                                        onChange={() =>
                                          setValue("between", !watch("between"))
                                        }
                                      />
                                    )}
                                  />
                                </Index.Box>
                                <Index.Typography className="checkbox-lable">
                                  Between
                                </Index.Typography>
                              </Index.Box>
                            </Index.Box>
                          </Index.Box>
                        </Index.Box>
                        {watch("between") && (
                          <>
                            <Index.Box className="input-box stock-report-input-box webview-input-box">
                              <Index.Box className="form-group">
                                <Index.FormHelperText className="lable-control">
                                  From Day
                                </Index.FormHelperText>
                                <Index.Box className="dropdown-box auto-drop-box">
                                  <Index.TextField
                                    fullWidth
                                    type="number"
                                    id="fullWidth"
                                    className="custom-form-control"
                                    // value={watch("brokerage")}
                                    {...register("fromDay", {
                                      required: "from day is required.",
                                    })}
                                    onChange={(e) => {
                                      // setValue("brokerage", e.target.value);
                                    }}
                                    error={Boolean(errors.fromDay)}
                                  />
                                </Index.Box>
                              </Index.Box>
                            </Index.Box>

                            <Index.Box className="input-box stock-report-input-box webview-input-box">
                              <Index.Box className="form-group">
                                <Index.FormHelperText className="lable-control">
                                  To Day
                                </Index.FormHelperText>
                                <Index.Box className="dropdown-box auto-drop-box">
                                  <Index.TextField
                                    fullWidth
                                    type="number"
                                    id="fullWidth"
                                    className="custom-form-control"
                                    // value={watch("brokerage")}
                                    {...register("toDay", {
                                      required: "to day is required.",
                                    })}
                                    onChange={(e) => {
                                      // setValue("brokerage", e.target.value);
                                    }}
                                    error={Boolean(errors.toDay)}
                                  />
                                </Index.Box>
                              </Index.Box>
                            </Index.Box>
                          </>
                        )}
                      </Index.Box>
                    </Index.Box>

                    <Index.Box className="input-box stock-report-input-box webview-input-box">
                      <Index.Box className="form-group">
                        <Index.Box className="checkbox-content-flex">
                          <Index.Box className="checkbox-main">
                            <Controller
                              className="checkbox"
                              control={control}
                              {...register("IgnoreCrDays")}
                              render={({ field: { onChange, value } }) => (
                                <input
                                  type="checkbox"
                                  className="custom-form-control checkbox"
                                  checked={watch("IgnoreCrDays") ? true : false}
                                  onChange={() =>
                                    setValue(
                                      "IgnoreCrDays",
                                      !watch("IgnoreCrDays")
                                    )
                                  }
                                  disabled={watch("show") !== "outstanding"}
                                />
                              )}
                            />
                          </Index.Box>
                          <Index.Typography className="checkbox-lable">
                            Ignore Cr. Days
                          </Index.Typography>
                        </Index.Box>
                      </Index.Box>
                    </Index.Box>

                    <Index.Box className="input-box stock-report-input-box webview-input-box">
                      <Index.Box className="form-group">
                        <Index.Box className="checkbox-content-flex">
                          <Index.Box className="checkbox-main">
                            <Controller
                              className="checkbox"
                              control={control}
                              {...register("printWithPaymentDetails")}
                              render={({ field: { onChange, value } }) => (
                                <input
                                  type="checkbox"
                                  className="custom-form-control checkbox"
                                  checked={
                                    watch("printWithPaymentDetails")
                                      ? true
                                      : false
                                  }
                                  onChange={() =>
                                    setValue(
                                      "printWithPaymentDetails",
                                      !watch("printWithPaymentDetails")
                                    )
                                  }
                                />
                              )}
                            />
                          </Index.Box>
                          <Index.Typography className="checkbox-lable">
                            Print With Payment Details
                          </Index.Typography>
                        </Index.Box>
                      </Index.Box>
                    </Index.Box>

                    {watch("outStandingReport") == "supplier" && (
                      <Index.Box className="input-box stock-report-input-box webview-input-box">
                        <Index.Box className="form-group">
                          <Index.Box className="checkbox-content-flex">
                            <Index.Box className="checkbox-main">
                              <Controller
                                className="checkbox"
                                control={control}
                                {...register("poNumber")}
                                render={({ field: { onChange, value } }) => (
                                  <input
                                    type="checkbox"
                                    className="custom-form-control checkbox"
                                    checked={watch("poNumber") ? true : false}
                                    onChange={() =>
                                      setValue("poNumber", !watch("poNumber"))
                                    }
                                  />
                                )}
                              />
                            </Index.Box>
                            <Index.Typography className="checkbox-lable">
                              P.O. Number
                            </Index.Typography>
                          </Index.Box>
                        </Index.Box>
                      </Index.Box>
                    )}

                    <Index.Box className="input-box stock-report-input-box webview-input-box">
                      <Index.Box className="form-group">
                        <Index.Box className="checkbox-content-flex">
                          <Index.Box className="checkbox-main">
                            <Controller
                              className="checkbox"
                              control={control}
                              {...register("category")}
                              render={({ field: { onChange, value } }) => (
                                <input
                                  type="checkbox"
                                  className="custom-form-control checkbox"
                                  checked={watch("category") ? true : false}
                                  onChange={() =>
                                    setValue("category", !watch("category"))
                                  }
                                />
                              )}
                            />
                          </Index.Box>
                          <Index.Typography className="checkbox-lable">
                            Category
                          </Index.Typography>
                        </Index.Box>
                      </Index.Box>
                    </Index.Box>

                    <Index.Box className="input-box stock-report-input-box webview-input-box">
                      <Index.Box className="form-group">
                        <Index.Box className="checkbox-content-flex">
                          <Index.Box className="checkbox-main">
                            <Controller
                              className="checkbox"
                              control={control}
                              {...register("monthWiseTotal")}
                              render={({ field: { onChange, value } }) => (
                                <input
                                  type="checkbox"
                                  className="custom-form-control checkbox"
                                  checked={
                                    watch("monthWiseTotal") ? true : false
                                  }
                                  onChange={() =>
                                    setValue(
                                      "monthWiseTotal",
                                      !watch("monthWiseTotal")
                                    )
                                  }
                                />
                              )}
                            />
                          </Index.Box>
                          <Index.Typography className="checkbox-lable">
                            Month Wise Total
                          </Index.Typography>
                        </Index.Box>
                      </Index.Box>
                    </Index.Box>

                    <Index.Box className="input-box stock-report-input-box webview-input-box">
                      <Index.Box className="form-group">
                        <Index.Box className="checkbox-content-flex">
                          <Index.Box className="checkbox-main">
                            <Controller
                              className="checkbox"
                              control={control}
                              {...register("collectionLetter")}
                              render={({ field: { onChange, value } }) => (
                                <input
                                  type="checkbox"
                                  className="custom-form-control checkbox"
                                  checked={
                                    watch("collectionLetter") ? true : false
                                  }
                                  onChange={() =>
                                    setValue(
                                      "collectionLetter",
                                      !watch("collectionLetter")
                                    )
                                  }
                                />
                              )}
                            />
                          </Index.Box>
                          <Index.Typography className="checkbox-lable">
                            Outstanding Collection Letter
                          </Index.Typography>
                        </Index.Box>
                      </Index.Box>
                    </Index.Box>

                    <Index.Box className="input-box stock-report-input-box webview-input-box">
                      <Index.Box className="form-group">
                        <Index.Box className="checkbox-content-flex">
                          <Index.Box className="checkbox-main">
                            <Controller
                              className="checkbox"
                              control={control}
                              {...register("ignorePayment")}
                              render={({ field: { onChange, value } }) => (
                                <input
                                  type="checkbox"
                                  className="custom-form-control checkbox"
                                  checked={
                                    watch("ignorePayment") ? true : false
                                  }
                                  onChange={() =>
                                    setValue(
                                      "ignorePayment",
                                      !watch("ignorePayment")
                                    )
                                  }
                                />
                              )}
                            />
                          </Index.Box>
                          <Index.Typography className="checkbox-lable">
                            Ignore Payment
                          </Index.Typography>
                        </Index.Box>
                      </Index.Box>
                    </Index.Box>

                    <Index.Box className="input-box stock-report-input-box webview-input-box">
                      <Index.Box className="form-group">
                        <Index.Box className="checkbox-content-flex">
                          <Index.Box className="checkbox-main">
                            <Controller
                              className="checkbox"
                              control={control}
                              {...register("WithAdvance")}
                              render={({ field: { onChange, value } }) => (
                                <input
                                  type="checkbox"
                                  className="custom-form-control checkbox"
                                  checked={watch("WithAdvance") ? true : false}
                                  onChange={() =>
                                    setValue(
                                      "WithAdvance",
                                      !watch("WithAdvance")
                                    )
                                  }
                                />
                              )}
                            />
                          </Index.Box>
                          <Index.Typography className="checkbox-lable">
                            With Advance
                          </Index.Typography>
                        </Index.Box>
                      </Index.Box>
                    </Index.Box>

                    <Index.Box className="input-box stock-report-input-box webview-input-box">
                      <Index.Box className="form-group">
                        <Index.Box className="checkbox-content-flex">
                          <Index.Box className="checkbox-main">
                            <Controller
                              className="checkbox"
                              control={control}
                              {...register("printTel")}
                              render={({ field: { onChange, value } }) => (
                                <input
                                  type="checkbox"
                                  className="custom-form-control checkbox"
                                  checked={watch("printTel") ? true : false}
                                  onChange={() =>
                                    setValue("printTel", !watch("printTel"))
                                  }
                                />
                              )}
                            />
                          </Index.Box>
                          <Index.Typography className="checkbox-lable">
                            Print Tel/Fax Details
                          </Index.Typography>
                        </Index.Box>
                      </Index.Box>
                    </Index.Box>

                    {(watch("outStandingReport") == "customer" ||
                      watch("outStandingReport") == "supplier") && (
                        <Index.Box className="input-box stock-report-input-box webview-input-box">
                          <Index.Box className="form-group">
                            <Index.Box className="checkbox-content-flex">
                              <Index.Box className="checkbox-main">
                                <Controller
                                  className="checkbox"
                                  control={control}
                                  {...register("printOnLetter")}
                                  render={({ field: { onChange, value } }) => (
                                    <input
                                      type="checkbox"
                                      className="custom-form-control checkbox"
                                      checked={
                                        watch("printOnLetter") ? true : false
                                      }
                                      onChange={() =>
                                        setValue(
                                          "printOnLetter",
                                          !watch("printOnLetter")
                                        )
                                      }
                                    />
                                  )}
                                />
                              </Index.Box>
                              <Index.Typography className="checkbox-lable">
                                Print On Letter Head
                              </Index.Typography>
                            </Index.Box>
                          </Index.Box>
                        </Index.Box>
                      )}

                    {(watch("outStandingReport") == "customeragewise" ||
                      watch("outStandingReport") == "supplieragewise") && (
                        <>
                          <Index.Box className="input-box stock-report-input-box webview-input-box">
                            <Index.Box className="form-group">
                              <Index.Box className="checkbox-content-flex">
                                <Index.Box className="checkbox-main">
                                  <Controller
                                    className="checkbox"
                                    control={control}
                                    {...register("AgeWiseAnalysis", {
                                      required:
                                        watch("ageSummary") == false &&
                                          (watch("outStandingReport") ==
                                            "customeragewise" ||
                                            watch("outStandingReport") ==
                                            "supplieragewise")
                                          ? "Age Wise Analysis or Age Summary is required."
                                          : false,
                                    })}
                                    error={
                                      watch("ageSummary") == false &&
                                        (watch("outStandingReport") ==
                                          "customeragewise" ||
                                          watch("outStandingReport") ==
                                          "supplieragewise")
                                        ? Boolean(errors.AgeWiseAnalysis)
                                        : false
                                    }
                                    render={({ field: { onChange, value } }) => (
                                      <input
                                        type="checkbox"
                                        error={
                                          watch("ageSummary") == false &&
                                            (watch("outStandingReport") ==
                                              "customeragewise" ||
                                              watch("outStandingReport") ==
                                              "supplieragewise")
                                            ? Boolean(errors?.AgeWiseAnalysis)
                                            : false
                                        }
                                        className="custom-form-control checkbox"
                                        checked={
                                          watch("AgeWiseAnalysis") ? true : false
                                        }
                                        onChange={() => {
                                          setValue(
                                            "AgeWiseAnalysis",
                                            !watch("AgeWiseAnalysis")
                                          );
                                          setValue(
                                            "ageSummary",
                                            watch("ageSummary")
                                              ? !watch("ageSummary")
                                              : ""
                                          );
                                        }}
                                      />
                                    )}
                                  />
                                </Index.Box>
                                <Index.Typography className="checkbox-lable">
                                  Age Wise Analysis
                                </Index.Typography>
                              </Index.Box>
                            </Index.Box>
                          </Index.Box>

                          <Index.Box className="input-box stock-report-input-box webview-input-box">
                            <Index.Box className="form-group">
                              <Index.Box className="checkbox-content-flex">
                                <Index.Box className="checkbox-main">
                                  <Controller
                                    className="checkbox"
                                    control={control}
                                    {...register("ageSummary", {
                                      required:
                                        watch("AgeWiseAnalysis") == false &&
                                          (watch("outStandingReport") ==
                                            "customeragewise" ||
                                            watch("outStandingReport") ==
                                            "supplieragewise")
                                          ? "ageSummary is required."
                                          : false,
                                    })}
                                    error={
                                      watch("AgeWiseAnalysis") == false &&
                                        (watch("outStandingReport") ==
                                          "customeragewise" ||
                                          watch("outStandingReport") ==
                                          "supplieragewise")
                                        ? Boolean(errors.AgeWiseAnalysis)
                                        : false
                                    }
                                    render={({ field: { onChange, value } }) => (
                                      <input
                                        type="checkbox"
                                        className="custom-form-control checkbox"
                                        checked={
                                          watch("ageSummary") ? true : false
                                        }
                                        onChange={() => {
                                          setValue(
                                            "ageSummary",
                                            !watch("ageSummary")
                                          );
                                          setValue(
                                            "AgeWiseAnalysis",
                                            watch("AgeWiseAnalysis")
                                              ? !watch("AgeWiseAnalysis")
                                              : ""
                                          );
                                        }}
                                      />
                                    )}
                                  />
                                </Index.Box>
                                <Index.Typography className="checkbox-lable">
                                  Age Summary
                                </Index.Typography>
                              </Index.Box>
                            </Index.Box>
                          </Index.Box>
                        </>
                      )}

                    {(watch("outStandingReport") == "customerbroker" ||
                      watch("outStandingReport") == "supplierbroker") && (
                        <Index.Box className="input-box stock-report-input-box webview-input-box">
                          <Index.Box className="form-group">
                            <Index.Box className="checkbox-content-flex">
                              <Index.Box className="checkbox-main">
                                <Controller
                                  className="checkbox"
                                  control={control}
                                  {...register("subBroker")}
                                  render={({ field: { onChange, value } }) => (
                                    <input
                                      type="checkbox"
                                      className="custom-form-control checkbox"
                                      checked={watch("subBroker") ? true : false}
                                      onChange={() =>
                                        setValue("subBroker", !watch("subBroker"))
                                      }
                                    />
                                  )}
                                />
                              </Index.Box>
                              <Index.Typography className="checkbox-lable">
                                Sub Broker
                              </Index.Typography>
                            </Index.Box>
                          </Index.Box>
                        </Index.Box>
                      )}

                    {(watch("AgeWiseAnalysis") || watch("ageSummary")) && (
                      <>
                        <Index.Box className="input-box stock-report-input-box webview-input-box">
                          <Index.Box className="form-group">
                            <Index.FormHelperText className="lable-control">
                              1St Period
                            </Index.FormHelperText>
                            <Index.Box className="dropdown-box auto-drop-box">
                              <Index.TextField
                                fullWidth
                                type="number"
                                id="fullWidth"
                                className="custom-form-control"
                                value={watch("firstPeriod")}
                                {...register("firstPeriod", {
                                  required:
                                    watch("AgeWiseAnalysis") ||
                                      watch("ageSummary")
                                      ? "start date is required."
                                      : false,
                                })}
                                error={
                                  watch("AgeWiseAnalysis") ||
                                    watch("ageSummary")
                                    ? Boolean(errors.firstPeriod)
                                    : false
                                }
                                onChange={(e) => {
                                  const value = Math.max(
                                    0,
                                    Number(e.target.value)
                                  );
                                  setValue("firstPeriod", value);
                                  setValue("secondPeriod", "");
                                  setValue("thirdPeriod", "");
                                  setValue("fourthPeriod", "");
                                }}
                              />
                            </Index.Box>
                          </Index.Box>
                        </Index.Box>

                        <Index.Box className="input-box stock-report-input-box webview-input-box">
                          <Index.Box className="form-group">
                            <Index.FormHelperText className="lable-control">
                              2nd Period
                            </Index.FormHelperText>
                            <Index.Box className="dropdown-box auto-drop-box">
                              <Index.TextField
                                fullWidth
                                type="number"
                                id="fullWidth"
                                className="custom-form-control"
                                {...register("secondPeriod", {
                                  required:
                                    watch("AgeWiseAnalysis") ||
                                      watch("ageSummary")
                                      ? "secondPeriod is required."
                                      : false,
                                })}
                                error={
                                  watch("AgeWiseAnalysis") ||
                                    watch("ageSummary")
                                    ? Boolean(errors.secondPeriod)
                                    : false
                                }
                                onChange={(e) => {
                                  const firstPeriod =
                                    Number(watch("firstPeriod")) || 0;
                                  const value = Math.max(
                                    firstPeriod,
                                    Number(e.target.value)
                                  );
                                  setValue(
                                    "secondPeriod",
                                    Number(e.target.value)
                                  );
                                  setValue("thirdPeriod", "");
                                  setValue("fourthPeriod", "");
                                }}
                              // error={Boolean(errors.letter)}
                              />
                            </Index.Box>
                          </Index.Box>
                        </Index.Box>

                        <Index.Box className="input-box stock-report-input-box webview-input-box">
                          <Index.Box className="form-group">
                            <Index.FormHelperText className="lable-control">
                              3rd Period
                            </Index.FormHelperText>
                            <Index.Box className="dropdown-box auto-drop-box">
                              <Index.TextField
                                fullWidth
                                type="number"
                                id="fullWidth"
                                className="custom-form-control"
                                {...register("thirdPeriod", {
                                  required:
                                    watch("AgeWiseAnalysis") ||
                                      watch("ageSummary")
                                      ? "thirdPeriod is required."
                                      : false,
                                })}
                                error={
                                  watch("AgeWiseAnalysis") ||
                                    watch("ageSummary")
                                    ? Boolean(errors.thirdPeriod)
                                    : false
                                }
                                onChange={(e) => {
                                  const secondPeriod =
                                    Number(watch("secondPeriod")) || 0;
                                  const value = Math.max(
                                    secondPeriod,
                                    Number(e.target.value)
                                  );
                                  setValue(
                                    "thirdPeriod",
                                    Number(e.target.value)
                                  );
                                  setValue("fourthPeriod", "");
                                }}
                              // error={Boolean(errors.letter)}
                              />
                            </Index.Box>
                          </Index.Box>
                        </Index.Box>

                        <Index.Box className="input-box stock-report-input-box webview-input-box">
                          <Index.Box className="form-group">
                            <Index.FormHelperText className="lable-control">
                              4th Period
                            </Index.FormHelperText>
                            <Index.Box className="dropdown-box auto-drop-box">
                              <Index.TextField
                                fullWidth
                                type="number"
                                id="fullWidth"
                                className="custom-form-control"
                                // value={watch("brokerage")}
                                {...register("fourthPeriod", {
                                  required:
                                    watch("AgeWiseAnalysis") ||
                                      watch("ageSummary")
                                      ? "fourthPeriod is required."
                                      : false,
                                })}
                                error={
                                  watch("AgeWiseAnalysis") ||
                                    watch("ageSummary")
                                    ? Boolean(errors.thirdPeriod)
                                    : false
                                }
                                onChange={(e) => {
                                  const thirdPeriod =
                                    Number(watch("thirdPeriod")) || 0;
                                  const value = Math.max(
                                    thirdPeriod,
                                    Number(e.target.value)
                                  );
                                  setValue(
                                    "fourthPeriod",
                                    Number(e.target.value)
                                  );
                                }}
                              // error={Boolean(errors.letter)}
                              />
                            </Index.Box>
                          </Index.Box>
                        </Index.Box>
                      </>
                    )}
                    <Index.Box className="btn-flex-end ">
                      <Index.Box className="btn-primary-main webview-button-container">
                        <Index.Button
                          variant=""
                          className="btn-primary webview-submit-btn"
                          type="submit"
                        >
                          {isLoading ? (
                            <Index.Box className="webview-spinner-main">
                              <Index.CircularProgress className="webview-spinner"></Index.CircularProgress>
                            </Index.Box>
                          ) : (
                            "Submit"
                          )}
                        </Index.Button>
                      </Index.Box>
                    </Index.Box>
                  </form>
                  {/* {filterReportData?.length ? (
                    <Index.Box className="btn-flex-end">
                      <Index.Box className="btn-primary-main">
                        <ContainerIndex.LinearButton
                          btnLabel="Print"
                          className="btn-primary"
                          onClick={() => handlePrint()}
                        ></ContainerIndex.LinearButton>
                      </Index.Box>{" "}
                      <Index.Box className="btn-primary-main">
                        <ContainerIndex.LinearButton
                          btnLabel="Excel"
                          className="btn-primary"
                          onClick={() => exportToExcel()}
                        ></ContainerIndex.LinearButton>
                      </Index.Box>
                    </Index.Box>
                  ) : (
                    <></>
                  )} */}
                  {watch("isSubmitted") == 1 && reportContent()}
                </Index.Box>
              </Index.Box>
            </Index.Box>
          </Index.Box>
        </Index.Box>
        {/* {filterReportData?.length ? (
          <Index.Box className="btn-flex-space">
            <Index.Box className="btn-flex-end">
        <button className="go-to-top-btn" onClick={() => handleScrollToTop()}>Go To Top</button>
              <Index.Box className="btn-primary-main">
                <ContainerIndex.LinearButton
                  btnLabel="Print"
                  className="btn-primary"
                  onClick={() => handlePrint()}
                ></ContainerIndex.LinearButton>
              </Index.Box>{" "}
              <Index.Box className="btn-primary-main">
                <ContainerIndex.LinearButton
                  btnLabel="Excel"
                  className="btn-primary"
                  onClick={() => exportToExcel()}
                ></ContainerIndex.LinearButton>
              </Index.Box>
            </Index.Box>
          </Index.Box>
        ) : (
          <></>
        )} */}
      </>
    );
  }
}
